// Wave 4 — Editor feature panels (ActionButton, SalesNotification, ...)
import { getLanguage } from './languageManager';
import { tx, sourceMessages } from './sourceTranslator';

type Dict = Record<string, string>;

const ptBR: Dict = {
  // Common editor pieces
  'panel.activateToConfigure': 'Ative o recurso para configurar',

  // ActionButton
  'ab.title': 'Botão de Ação',
  'ab.desc': 'Direcione seus espectadores com um CTA no momento certo.',
  'ab.config': 'Configuração do Botão',
  'ab.config.desc': 'Defina o texto e link do seu CTA.',
  'ab.text': 'Texto do botão',
  'ab.link': 'Link do botão',
  'ab.appearance': 'Aparência',
  'ab.appearance.desc': 'Personalize as cores do botão.',
  'ab.bg': 'Fundo',
  'ab.fontColor': 'Texto',
  'ab.colorButton': 'Cor do Botão',
  'ab.colorText': 'Cor do Texto',
  'ab.showTime': 'Tempo de Exibição',
  'ab.showTime.desc': 'Defina quando o botão aparecerá no vídeo.',
  'ab.seconds': 'segundos',

  // SalesNotification
  'sn.enable': 'Notificação de Vendas',
  'sn.enable.helper': 'Mostre vendas recentes para criar prova social.',
  'sn.tab.basic': 'Configurações',
  'sn.tab.content': 'Conteúdo',
  'sn.tab.appearance': 'Aparência',
  'sn.activeStatusTpl': 'Ativo · 1 notificação a cada {freq}s · {pos}',
  'sn.speed.title': 'Velocidade das Notificações',
  'sn.speed.startDelay': 'Atraso inicial',
  'sn.speed.frequency': 'Frequência',
  'sn.speed.duration': 'Duração',
  'sn.position.title': 'Posição',
  'sn.pos.top-left': 'Superior Esquerda',
  'sn.pos.top-right': 'Superior Direita',
  'sn.pos.bottom-left': 'Inferior Esquerda',
  'sn.pos.bottom-right': 'Inferior Direita',
  'sn.loop.title': 'Repetir em loop',
  'sn.loop.desc': 'Reinicia automaticamente após exibir todas.',
  'sn.interaction.title': 'Interação',
  'sn.interaction.openLink': 'Ao clicar, abrir link',
  'sn.interaction.openLinkDesc': 'Abre uma URL ao clicar na notificação.',
  'sn.interaction.urlPlaceholder': 'https://exemplo.com/oferta',
  'sn.interaction.urlHelp': 'Ao clicar na notificação, abrir esta URL em nova aba.',
  'sn.interaction.urlInvalid': 'URL inválida. Use http:// ou https://. Ex: https://seu-site.com',
  'sn.interaction.urlAutofix': 'Sem http:// — vamos salvar como https://{url}',
  'sn.sound.title': 'Som',
  'sn.sound.desc': 'Reproduz um som junto da notificação.',
  'sn.sound.volume': 'Volume',
  'sn.sound.test': 'Testar som',
  'sn.message.title': 'Mensagem',
  'sn.message.product': 'Nome do produto',
  'sn.message.productPlaceholder': 'Ex: Curso Premium',
  'sn.message.showPlatform': 'Mostrar plataforma',
  'sn.message.selectPlatform': 'Selecione uma plataforma',
  'sn.message.platformCustom': 'Personalizado',
  'sn.message.customLogo': 'Logo personalizado',
  'sn.message.changeLogo': 'Clique para trocar',
  'sn.message.addLogo': 'Adicionar logo',
  'sn.simulated.title': 'Dados simulados',
  'sn.simulated.names': 'Nomes',
  'sn.simulated.cities': 'Cidades',
  'sn.simulated.auto': 'Sugestão automática',
  'sn.simulated.recommended': '(recomendado)',
  'sn.simulated.custom': 'Lista personalizada',
  'sn.simulated.noLocation': 'Não exibir localização',
  'sn.simulated.typeEnter': 'Digite e pressione Enter',
  'sn.simulated.add10': 'Adicionar 10 exemplos',
  'sn.template.title': 'Texto do template',
  'sn.template.placeholder': '{nome} de {cidade} acabou de comprar {produto}',
  'sn.template.insert': 'Inserir:',
  'sn.template.preview': 'Prévia',
  'sn.appearance.title': 'Estilo',
  'sn.appearance.bg': 'Cor de fundo',
  'sn.appearance.border': 'Cor da borda',
  'sn.appearance.color': 'Cor da letra',
  'sn.appearance.radius': 'Arredondamento',
  'sn.appearance.showIcon': 'Mostrar ícone',
  'sn.quick.title': 'Ajustes rápidos',
  'sn.quick.size': 'Tamanho',
  'sn.size.small': 'Pequeno',
  'sn.size.medium': 'Médio',
  'sn.size.large': 'Grande',
  'sn.disclaimer': 'Essas configurações não afetam o funcionamento do vídeo.',
  'sn.colorCustom': 'Cor personalizada',
  'sn.addMore': 'Adicionar...',
  'sn.fallbackPos': 'Inferior Direita',

  // Border
  'border.title': 'Borda do Player',
  'border.desc': 'Destaque o player com borda e sombra.',
  'border.appearance': 'Aparência',
  'border.appearance.desc': 'Cor, espessura e arredondamento da borda.',
  'border.color': 'Cor da borda',
  'border.width': 'Espessura',
  'border.radius': 'Arredondamento',
  'border.clickToChange': 'Clique para alterar',
  'border.shadow.toggle': 'Ativar sombra',
  'border.shadow.toggleDesc': 'Adicione profundidade e destaque ao player.',
  'border.shadow.title': 'Configurações de Sombra',
  'border.shadow.desc': 'Ajuste cor, desfoque e intensidade.',
  'border.shadow.color': 'Cor da sombra',
  'border.shadow.blur': 'Desfoque',
  'border.shadow.intensity': 'Intensidade',

  // Controls
  'controls.title': 'Controles do Vídeo',
  'controls.desc': 'Permita interação com o vídeo e aumente controle, retenção e experiência do usuário.',
  'controls.loopBehavior': 'Comportamento de Loop',
  'controls.loopBehavior.desc': 'Define se o vídeo repete automaticamente.',
  'controls.noRepeat': 'Não repetir',
  'controls.repeat': 'Repetir',

  // AbsencePause
  'ap.title': 'Pausa Inteligente',
  'ap.desc': 'Pausa o vídeo quando o espectador sai da aba.',
  'ap.helper': 'Funciona quando o visitante troca de aba ou minimiza o navegador.',
  'ap.on': 'Pausa inteligente ativada',
  'ap.off': 'Pausa inteligente desativada',
  'ap.active': 'Ativo',
  'ap.onDesc': 'O vídeo pausa ao sair da aba e retoma automaticamente ao retornar.',
  'ap.offDesc': 'Ative o recurso acima para pausar o vídeo quando o visitante sair da aba.',

  // RestartAfterEnd
  'rae.title': 'Recomeçar o vídeo ao finalizar',
  'rae.desc': 'Volta o vídeo ao início quando a reprodução termina.',
  'rae.helper': 'Funciona ao final da reprodução do vídeo.',
  'rae.on': 'Reinício automático ativado',
  'rae.off': 'Reinício automático desativado',
  'rae.onDesc': 'Ao finalizar, o vídeo volta ao início automaticamente, mantendo a reprodução em loop.',
  'rae.offDesc': 'Ative o recurso acima para reiniciar o vídeo automaticamente ao chegar ao fim.',

  // Affiliate
  'aff.title': 'Ativar link de afiliado',
  'aff.desc': 'Exibe a logo HostVSL e ativa seu link de afiliado.',
  'aff.linkLabel': 'Seu link de afiliado',
  'aff.linkPlaceholder': 'https://hostvsl.com/?ref=SEU_ID',
  'aff.linkInvalid': 'Parece que este link não é válido. Verifique e tente novamente.',
  'aff.linkHelper': 'Insira seu link de afiliado para ganhar comissões. A logo da HostVSL ficará disponível no canto superior direito do player e, ao clicar nela, seu link de afiliado será aberto em uma nova aba.',
  'aff.previewTitle': 'Logo da HostVSL será exibida no player.',
  'aff.previewClick': 'Clique do usuário abrirá:',

  // Pixel
  'pix.tokenCopied': 'Token copiado!',
  'pix.facebook': 'Facebook',

  // Continue Watching
  'cw.title': 'Continuar de onde parou',
  'cw.desc': 'Permite que usuários retomem o vídeo de onde pararam',
  'cw.tab.templates': 'Templates',
  'cw.tab.customize': 'Personalização',
  'cw.templates.helper': 'Selecione um template para a tela de retorno',
  'cw.templates.resume': 'Retomar',
  'cw.upload.title': 'Upload Personalizado',
  'cw.upload.subtitle': 'Arraste seu arquivo ou clique para enviar',
  'cw.upload.maxSize': 'Tamanho máximo: 2MB',
  'cw.upload.swap': 'Trocar',
  'cw.upload.remove': 'Remover',
  'cw.customize.soon': 'Opções de personalização em breve.',

  // Thumbnails
  'th.title': 'Thumbnails do Vídeo',
  'th.desc': 'Defina imagens estratégicas para reforçar a atenção.',
  'th.subhint': 'Início, pausa e final podem ter imagens diferentes.',
  'th.tab.initial': 'Início',
  'th.tab.pause': 'Pausa',
  'th.tab.end': 'Final',
  'th.toast.added.title': 'Thumbnail adicionada com sucesso',
  'th.toast.added.initial': 'A thumbnail de início foi configurada',
  'th.toast.added.pause': 'Configure o botão de ação se desejar',
  'th.toast.added.end': 'A thumbnail de final foi configurada',
  'th.toast.removed.title': 'Thumbnail removida com sucesso',
  'th.toast.removed.desc': 'A thumbnail foi removida',
  'th.toast.mode.title': 'Modo atualizado',
  'th.toast.mode.always': 'Thumbnail será exibida sempre que pausar',
  'th.toast.mode.range': 'Thumbnail será exibida apenas no trecho específico',
  'th.toast.button.added.title': 'Botão adicionado',
  'th.toast.button.added.desc': 'Configure o botão e visualize no preview',
  'th.toast.button.removed.title': 'Botão removido',
  'th.toast.button.removed.desc': 'O botão foi removido do thumbnail de pausa',
  'th.button.defaultText': 'Continue Assistindo',

  // Final Countdown
  'fc.title': 'Contagem Final Inteligente',
  'fc.desc': 'Exibe uma contagem regressiva ao final do vídeo e redireciona automaticamente.',
  'fc.tab.settings': 'Configurações',
  'fc.tab.style': 'Estilo',
  'fc.essential': 'Essencial',
  'fc.timeLabel': 'Tempo da contagem',
  'fc.redirect': 'Redirecionamento',
  'fc.onEnd': 'Ao finalizar',
  'fc.action.redirect': 'Redirecionar automaticamente ao terminar',
  'fc.action.warning': 'Apenas exibir o aviso',
  'fc.urlLabel': 'URL de destino',
  'fc.urlPlaceholder': 'https://seu-checkout.com',
  'fc.url.empty': 'Informe uma URL para ativar o redirecionamento.',
  'fc.url.invalid': 'URL inválida. Ex: https://seu-site.com',
  'fc.url.autofix': 'Sem http:// — vamos salvar como https://{url}',
  'fc.button.noTarget': 'Sem uma URL de redirecionamento válida o botão não aparece para o espectador.',
  'fc.dismiss': 'Fechar e voltar ao vídeo',
  'fc.linkOpen': 'Abertura do link',
  'fc.openSame': 'Abrir na mesma aba',
  'fc.openNew': 'Abrir em nova aba',
  'fc.warningOnly': 'Essas opções são usadas apenas quando o redirecionamento automático estiver ativo.',
  'fc.cardContent': 'Conteúdo do Card',
  'fc.cardTitle': 'Título do Card',
  'fc.cardTitlePlaceholder': 'Oferta Especial Liberada!',
  'fc.cardDesc': 'Descrição',
  'fc.cardDescPlaceholder': 'Você será redirecionado em alguns segundos…',
  'fc.cardDescHelp': 'A contagem será exibida automaticamente com base no tempo definido.',
  'fc.button': 'Botão "Ir Agora"',
  'fc.button.show': 'Exibir botão',
  'fc.button.text': 'Texto do botão',
  'fc.button.textPlaceholder': 'Ir Agora',
  'fc.button.action': 'Ação do botão',
  'fc.appearance': 'Aparência',
  'fc.displayMode': 'Modo de exibição',
  'fc.mode.overlay': 'Sobrepor no vídeo',
  'fc.mode.fullscreen': 'Tela cheia',
  'fc.preset': 'Preset de estilo',
  'fc.preset.host': 'Padrão Host',
  'fc.preset.host.sub': 'Recomendado',
  'fc.preset.minimal': 'Discreto',
  'fc.preset.minimal.sub': 'Suave e limpo',
  'fc.quick': 'Ajustes rápidos',
  'fc.quick.opacity': 'Opacidade do fundo',
  'fc.quick.radius': 'Raio do card',
  'fc.quick.mainColor': 'Cor principal',
  'fc.quick.mainColorName': 'Cor Principal',
  'fc.quick.mainColorHelp': 'Aplicada no botão e detalhes',
  'fc.quick.bgColor': 'Cor do fundo',
  'fc.quick.customColor': 'Cor Personalizada',
  'fc.count': 'Contagem',
  'fc.count.circle': 'Cor do Círculo',
  'fc.count.number': 'Cor do Número',
  'fc.count.size': 'Tamanho',
  'fc.size.s': 'P',
  'fc.size.m': 'M',
  'fc.size.l': 'G',
  'fc.typography': 'Tipografia',
  'fc.typo.title': 'Cor do Título',
  'fc.typo.desc': 'Cor da Descrição',
  'fc.btnGroup': 'Botão',
  'fc.btn.color': 'Cor do Botão',
  'fc.btn.text': 'Cor do Texto',
  'fc.btn.radius': 'Raio',

  // Logo
  'lg.title': 'Logo',
  'lg.desc': 'Exiba sua logomarca sobre o vídeo.',
  'lg.tab.config': 'Configurações',
  'lg.tab.appearance': 'Aparência',
  'lg.upload': 'Upload da Logo',
  'lg.uploadError.format': 'Formato não suportado. Use PNG ou JPG.',
  'lg.uploadError.size': 'Arquivo maior que {n}MB',
  'lg.uploadError.generic': 'Erro ao processar arquivo',
  'lg.link': 'Link de destino',
  'lg.link.placeholder': 'seusite.com',
  'lg.link.invalid': 'URL inválida',
  'lg.link.invalidDomain': 'Digite um domínio válido. Ex: seusite.com',
  'lg.link.savedAs': '→ Será salvo como:',
  'lg.link.helper': 'Aceita: seusite.com, www.seusite.com ou https://seusite.com',
  'lg.utm.enable': 'Adicionar parâmetros UTM',
  'lg.utm.source': 'utm_source (ex: youtube)',
  'lg.utm.medium': 'utm_medium (ex: video)',
  'lg.utm.campaign': 'utm_campaign (ex: lancamento)',
  'lg.posSize': 'Posição e Tamanho',
  'lg.posLabel': 'Posição no player',
  'lg.fineTune': 'Ajustes finos',
  'lg.offsetX': 'Deslocamento Horizontal',
  'lg.offsetY': 'Deslocamento Vertical',
  'lg.sizeOpacity': 'Tamanho e Transparência',
  'lg.size': 'Tamanho',
  'lg.opacity': 'Transparência',
  'lg.border.add': 'Adicionar borda',
  'lg.border.color': 'Cor da borda',
  'lg.border.width': 'Espessura',
  'lg.shadow.add': 'Adicionar sombra',
  'lg.shadow.color': 'Cor da sombra',
  'lg.shadow.blur': 'Desfoque',
  'lg.shadow.intensity': 'Intensidade',
  'lg.pos.top-left': 'Superior Esquerda',
  'lg.pos.top-center': 'Superior Centro',
  'lg.pos.top-right': 'Superior Direita',
  'lg.pos.center-left': 'Centro Esquerda',
  'lg.pos.center': 'Centro',
  'lg.pos.center-right': 'Centro Direita',
  'lg.pos.bottom-left': 'Inferior Esquerda',
  'lg.pos.bottom-center': 'Inferior Centro',
  'lg.pos.bottom-right': 'Inferior Direita',
  // Mini-Hook
  'mh.title': 'Mini-Hook',
  'mh.desc': 'Exiba imagens ou GIFs em momentos estratégicos.',
  'mh.image': 'Imagem',
  'mh.delete': 'Excluir',
  'mh.empty.title': 'Comece com seu primeiro Mini-Hook',
  'mh.empty.desc': 'Use Mini-Hooks para exibir imagens ou GIFs em momentos estratégicos do vídeo. Ideal para reforçar promessas, criar avisos visuais e recuperar atenção.',
  'mh.empty.cta': 'Criar primeiro Mini-Hook',
  'mh.count.suffix': 'Mini-Hook',
  'mh.count.suffixPlural': 'Mini-Hooks',
  'mh.activeSuffix': 'ativo',
  'mh.activeSuffixPlural': 'ativos',
  'mh.new': 'Novo Mini-Hook',
  'mh.search': 'Buscar Mini-Hook',
  'mh.back': 'Voltar',
  'mh.name': 'Nome interno',
  'mh.name.placeholder': 'Ex: Reforço da promessa inicial',
  'mh.imageOrGif': 'Imagem ou GIF',
  'mh.upload.cta': 'Clique ou arraste o arquivo aqui',
  'mh.upload.formats': 'JPG, PNG, WEBP e GIF • até 20 MB',
  'mh.file.default': 'Arquivo',
  'mh.file.gif': 'GIF animado',
  'mh.file.image': 'Imagem estática',
  'mh.file.remove': 'Remover',
  'mh.file.swap': 'Trocar',
  'mh.opt.analyzing': 'Analisando…',
  'mh.opt.optimizing': 'Otimizando…',
  'mh.opt.light': 'Arquivo leve',
  'mh.opt.heavy': 'Pode impactar performance',
  'mh.opt.heavyGif': 'Este GIF é pesado ({size}) e pode impactar a fluidez do preview. Recomendamos usar um GIF mais leve ou reduzir as dimensões.',
  'mh.opt.auto': 'Otimizar automaticamente',
  'mh.opt.autoDesc': 'GIFs otimizados deixam o Mini-Hook mais fluido.',
  'mh.timing': 'Temporização',
  'mh.timing.start': 'Aparecer em',
  'mh.timing.end': 'Desaparecer em',
  'mh.opacity': 'Opacidade',
  'mh.cancel': 'Cancelar',
  'mh.save': 'Salvar alterações',
  'mh.create': 'Criar Mini-Hook',
  'mh.toast.tooLarge': 'Arquivo muito grande. Máximo: 20 MB',
  'mh.toast.invalidFormat': 'Formato inválido. Use JPG, PNG, WEBP ou GIF.',
  'mh.toast.optimized': 'GIF otimizado • {n}% mais leve',
  'mh.toast.heavyWarn': 'GIF pesado pode impactar a fluidez do preview.',
  'mh.toast.updated': 'Mini-Hook atualizado com sucesso',
  'mh.toast.created': 'Mini-Hook criado com sucesso',
  'mh.toast.duplicated': 'Mini-Hook duplicado com sucesso',
  'mh.toast.removed': 'Mini-Hook removido com sucesso',
  'mh.toast.overlap': 'Este Mini-Hook se sobrepõe a "{name}" no mesmo intervalo.',
  'mh.copy.suffix': '(cópia)',
  'mh.validate.upload': 'Envie uma imagem ou GIF para continuar.',
  'mh.validate.name': 'Digite um nome para identificar este Mini-Hook.',
  'mh.validate.endGtStart': 'O tempo de saída precisa ser maior que o tempo de entrada.',
  'mh.confirm.title': 'Excluir Mini-Hook?',
  'mh.confirm.desc': 'Tem certeza que deseja excluir este Mini-Hook? Esta ação não pode ser desfeita.',
  'mh.confirm.cancel': 'Cancelar',
  'mh.confirm.delete': 'Excluir',
  // Subtitles
  'sb.title': 'Legendas',
  'sb.desc': 'Adicione legendas para melhorar a acessibilidade.',
  'sb.empty.title': 'Configure suas legendas',
  'sb.empty.desc': 'Com as legendas ativadas, você pode adicionar arquivos .SRT ou .VTT ao seu vídeo. O espectador poderá ativar ou desativar a legenda pelo botão CC nos controles.',
  'sb.empty.hint': 'Ative o switch acima para começar a configurar suas legendas.',
  'sb.help': 'Aprenda sobre legendas',
  'sb.help.tip': 'Envie um arquivo .VTT ou .SRT com as legendas sincronizadas. Ambos os formatos são suportados.',
  'sb.file.title': 'Arquivo da legenda',
  'sb.file.desc': 'Envie o arquivo .VTT ou .SRT com as legendas sincronizadas.',
  'sb.upload.cta': 'Clique ou arraste o arquivo .VTT ou .SRT',
  'sb.upload.formats': 'Formatos aceitos: .VTT, .SRT',
  'sb.upload.error': 'Arquivo inválido ou corrompido. Nenhuma legenda encontrada.',
  'sb.upload.template': 'Baixe o arquivo modelo para ver o formato correto',
  'sb.help.section': 'Precisa de ajuda para criar sua legenda?',
  'sb.help.download': 'Baixar modelo (.TXT)',
  'sb.help.downloadDesc': 'Baixe um exemplo simples para preencher suas legendas.',
  'sb.help.convert': 'Converter para VTT',
  'sb.help.convertDesc': 'Converta seu texto para o formato aceito.',
  'sb.file.loaded': 'Carregado',
  'sb.file.cuesOne': 'legenda',
  'sb.file.cuesMany': 'legendas',
  'sb.file.edit': 'Editar legenda',
  'sb.file.replace': 'Substituir arquivo',
  'sb.file.remove': 'Remover legenda',
  'sb.preview.title': 'Pré-visualização',
  'sb.preview.search': 'Buscar legenda...',
  'sb.preview.empty': 'Nenhuma legenda encontrada para "{q}"',
  'sb.preview.showLess': 'Mostrar menos',
  'sb.preview.showAll': 'Ver todas ({n})',
  'sb.cue.editText': 'Editar texto',
  'sb.cue.delete': 'Excluir legenda',
  'sb.cue.editTime': 'Clique para editar tempo',
  'sb.editor.title': 'Editar conteúdo VTT',
  'sb.editor.save': 'Salvar',
  'sb.editor.cancel': 'Cancelar',
  'sb.toast.timeInvalid': 'Tempo inválido. Use o formato m:ss.s e garanta que início < fim.',
  'sb.toast.timeUpdated': 'Tempo atualizado!',
  'sb.toast.textEmpty': 'O texto da legenda não pode estar vazio.',
  'sb.toast.textUpdated': 'Texto atualizado!',
  'sb.toast.confirmDelete': 'Tem certeza que deseja excluir esta legenda?',
  'sb.toast.lastRemoved': 'Última legenda removida. Arquivo excluído.',
  'sb.toast.deleted': 'Legenda excluída!',
  'sb.toast.reordered': 'Ordem atualizada!',
  'sb.settings.title': 'Configurações da legenda',
  'sb.settings.desc': 'Personalize a exibição das legendas no player.',
  'sb.settings.showByDefault': 'Exibir legenda por padrão',
  'sb.settings.showByDefaultDesc': 'A legenda já começa ativa quando o vídeo carregar',
  'sb.settings.ccButton': 'Mostrar botão de legenda',
  'sb.settings.ccButtonDesc': 'Permite que o espectador ative ou desative',
  'sb.settings.warn': 'A legenda ficará desativada para o espectador.',
  'sb.settings.fontSize': 'Tamanho da fonte',
  'sb.settings.align': 'Alinhamento',
  'sb.settings.textColor': 'Cor do texto',
  'sb.settings.bgColor': 'Cor do fundo',
  'sb.settings.bgOpacity': 'Opacidade do fundo',
  'sb.style.title': 'Estilo da legenda',
  'sb.style.desc': 'Escolha como a legenda aparece no player.',
  'sb.style.animType': 'Tipo de animação',
  'sb.anim.none': 'Padrão',
  'sb.anim.noneDesc': 'Sem animação',
  'sb.anim.fade': 'Fade suave',
  'sb.anim.fadeDesc': 'Transição suave',
  'sb.anim.pop': 'Pop / Destaque',
  'sb.anim.popDesc': 'Escala com impacto',
  'sb.anim.word': 'Palavra por palavra',
  'sb.anim.wordDesc': 'Estilo TikTok',
  'sb.style.highlight': 'Estilo do destaque',
  'sb.hl.background': 'Fundo colorido',
  'sb.hl.textOnly': 'Cor do texto',
  'sb.hl.scale': 'Escala leve',
  'sb.hl.color': 'Cor do destaque',
  // === Auto-generated W4 sweep ===
  'wx.headlines_subheadlines_what': '📋 O que são Headlines/Subheadlines?',
  'wx.confirm_delete_action': 'Deseja realmente excluir esta ação?',
  'wx.assista_esse_video': '"Assista esse vídeo"',
  'wx.ganhe_r_1_000_mes_vs_ganhe_dinheiro': '"Ganhe R$ 1.000/mês" vs "Ganhe dinheiro"',
  'wx.ultimas_24h_vagas_limitadas': '"Últimas 24h", "Vagas Limitadas"',
  'wx.div_seu_html_aqui_div': '<div>Seu HTML aqui</div>',
  'wx.h1_meu_titulo_personalizado_h1_10_cole_seu_codigo_html_aqui': '<h1>Meu título personalizado</h1>&#10;Cole seu código HTML aqui...',
  'wx.a_distribuicao_precisa_somar_100': 'A distribuição precisa somar 100%',
  'wx.a_reproducao_automatica_cria_a_sensacao_de_inicio_imediato_a': 'A reprodução automática cria a sensação de início imediato, aumentando o engajamento dos usuários. O vídeo começa sem som para cumprir as políticas dos navegadores, e o overlay incentiva a ativação do áudio.',
  'wx.a_soma_da_distribuicao_de_trafego_deve_ser_100': 'A soma da distribuição de tráfego deve ser 100%',
  'wx.adicionar_classe': 'Adicionar classe',
  'wx.aguarde_pelo_menos_7_dias_para_que_o_teste_colete_dados_sufi': 'Aguarde pelo menos 7 dias para que o teste colete dados suficientes',
  'wx.ajustamos_o_intervalo_para_manter_a_ordem_correta': 'Ajustamos o intervalo para manter a ordem correta.',
  'wx.ajuste_a_aparencia_do_overlay_de_autoplay': 'Ajuste a aparência do overlay de autoplay',
  'wx.ajuste_velocidade_e_visibilidade_dos_controles': 'Ajuste velocidade e visibilidade dos controles.',
  'wx.ajustes_finos': 'Ajustes finos',
  'wx.alterar_estilo': 'Alterar estilo',
  'wx.alterar_texto': 'Alterar texto',
  'wx.animacao_pulsar': 'Animação Pulsar',
  'wx.animacao_de_entrada': 'Animação de entrada',
  'wx.ao_ativar_a_headline_sera_exibida_diretamente_sobre_o_video_': 'Exibe a headline diretamente sobre o vídeo.',
  'wx.ao_finalizar_a_velocidade_com_melhor_engajamento_sera_defini': 'Ao finalizar, a velocidade com melhor engajamento será definida como principal.',
  'wx.aparencia': 'Aparência',
  'wx.aparencia_do_player': 'Aparência do Player',
  'wx.aparencia_dos_controles': 'Aparência dos Controles',
  'wx.aplicada_em_barra_de_controle_play_volume_e_botoes_principai': 'Aplicada em barra de controle, play, volume e botões principais',
  'wx.aplicada_em_icones_secundarios_e_textos_do_player': 'Aplicada em ícones secundários e textos do player',
  'wx.arraste_ou_clique_para_enviar': 'Arraste ou clique para enviar',
  'wx.arraste_seu_arquivo_ou_clique_para_enviar': 'Arraste seu arquivo ou clique para enviar',
  'wx.arraste_sua_imagem_de_fundo': 'Arraste sua imagem de fundo',
  'wx.assistir_do_inicio': 'Assistir do início',
  'wx.ativar_autoplay': 'Ativar Autoplay',
  'wx.ativar_acoes_sincronizadas': 'Ativar Ações Sincronizadas',
  'wx.ativar_continuar_assistindo': 'Ativar Continuar Assistindo',
  'wx.ativar_headline': 'Headline',
  'wx.ativar_velocidade': 'Ativar Velocidade',
  'wx.ativar_botao': 'Ativar botão',
  'wx.ativar_tema_do_player': 'Ativar tema do player',
  'wx.ativar_teste_automatico': 'Ativar teste automático',
  'wx.ative_o_autoplay_na_aba_configuracao_para_configurar_testes_': 'Ative o autoplay na aba Configuração para configurar testes A/B',
  'wx.ative_o_autoplay_para_ver_o_preview': 'Ative o autoplay para ver o preview',
  'wx.ative_o_som': 'Ative o som',
  'wx.ate': 'Até',
  'wx.aumente_a_retencao_com_um_video_gancho_no_autoplay': 'Aumente a retenção com um vídeo gancho no autoplay',
  'wx.avalie_o_contexto_do_seu_conteudo_videos_tecnicos_podem_func': 'Avalie o contexto do seu conteúdo - vídeos técnicos podem funcionar melhor em velocidades mais baixas',
  'wx.avancado': 'Avançado',
  'wx.avancar_10s': 'Avançar 10s',
  'wx.acao': 'Ação',
  'wx.acao_atualizada_com_sucesso': 'Ação atualizada com sucesso!',
  'wx.acao_criada_com_sucesso': 'Ação criada com sucesso!',
  'wx.acao_excluida_com_sucesso': 'Ação excluída com sucesso!',
  'wx.acoes': 'Ações',
  'wx.baixo_direita': 'Baixo Direita',
  'wx.baixo_esquerda': 'Baixo Esquerda',
  'wx.barra_inferior': 'Barra Inferior',
  'wx.barra_de_controles': 'Barra de Controles',
  'wx.barra_de_progresso': 'Barra de Progresso',
  'wx.botao_continuar': 'Botão continuar',
  'wx.botao_de_acao': 'Botão de Ação',
  'wx.botao_discreto_sobre_o_video': 'Botão discreto sobre o vídeo',
  'wx.botao_play_pequeno': 'Botão play pequeno',
  'wx.botao_reiniciar': 'Botão reiniciar',
  'wx.buscar_videos': 'Buscar vídeos...',
  'wx.c_no_botao': 'C. no Botão',
  'wx.c_no_botao_2': 'C. no botão',
  'wx.click': 'Click',
  'wx.click_here': 'Click Here',
  'wx.cole_seu_codigo_html_aqui_10_aceita_html_css_inline_e_script': 'Cole seu código HTML aqui...&#10;Aceita HTML, CSS inline e scripts leves.',
  'wx.como_ficara_no_video': 'Como ficará no vídeo',
  'wx.compare_no_minimo_3_velocidades_diferentes_para_melhores_res': 'Compare no mínimo 3 velocidades diferentes para melhores resultados',
  'wx.comportamento': 'Comportamento',
  'wx.comportamento_do_video_de_fundo': 'Comportamento do Vídeo de Fundo',
  'wx.configuracao_personalizada': 'Configuração Personalizada',
  'wx.configuracoes': 'Configurações',
  'wx.configuracoes_tecnicas': 'Configurações Técnicas',
  'wx.configure_como_o_autoplay_se_comporta': 'Configure como o autoplay se comporta',
  'wx.configure_os_botoes_de_reproducao_e_volume': 'Configure os botões de reprodução e volume.',
  'wx.configure_quando_a_headline_aparece_e_desaparece': 'Configure quando a headline aparece e desaparece',
  'wx.configure_tamanho_e_posicao_por_dispositivo': 'Configure tamanho e posição por dispositivo.',
  'wx.considere_o_publico_alvo_usuarios_mais_tecnicos_geralmente_s': 'Considere o público-alvo: usuários mais técnicos geralmente são mais receptivos a velocidades mais rápidas.',
  'wx.conteudo': 'Conteúdo',
  'wx.conteudo_html': 'Conteúdo HTML',
  'wx.conteudo_de_continuar_de_onde_parou': 'Conteúdo de Continuar de onde parou',
  'wx.continuar_assistindo': 'Continuar Assistindo',
  'wx.continuar_agora': 'Continuar agora',
  'wx.continuar_assistindo_2': 'Continuar assistindo?',
  'wx.continuar_de_onde_parou': 'Continuar de onde parou',
  'wx.contraste_adequado_com_o_fundo': 'Contraste adequado com o fundo',
  'wx.controle_de_volume': 'Controle de volume',
  'wx.controles': 'Controles',
  'wx.controles_principais': 'Controles Principais',
  'wx.controles_de_reproducao': 'Controles de Reprodução',
  'wx.controles_do_player': 'Controles do Player',
  'wx.controles_foram_desabilitados_automaticamente_ao_ativar_auto': 'Controles foram desabilitados automaticamente ao ativar autoplay',
  'wx.conversao': 'Conversão:',
  'wx.conversoes': 'Conversões',
  'wx.conversoes_dividido_por_visualizacoes': 'Conversões dividido por visualizações',
  'wx.cor': 'Cor',
  'wx.cor_primaria': 'Cor Primária',
  'wx.cor_secundaria': 'Cor Secundária',
  'wx.cor_solida': 'Cor Sólida',
  'wx.cor_da_borda': 'Cor da Borda',
  'wx.cor_da_subheadline': 'Cor da Subheadline',
  'wx.cor_de_fundo': 'Cor de Fundo',
  'wx.cor_de_fundo_dos_controles': 'Cor de fundo dos controles',
  'wx.cor_de_fundo_e_opacidade_da_barra_inferior': 'Cor de fundo e opacidade da barra inferior.',
  'wx.cor_do_botao': 'Cor do Botão',
  'wx.cor_do_fundo': 'Cor do Fundo',
  'wx.cor_do_texto': 'Cor do Texto',
  'wx.cor_do_icone': 'Cor do Ícone',
  'wx.cores_do_autoplay': 'Cores do Autoplay',
  'wx.cores_do_botao': 'Cores do Botão',
  'wx.cores_do_overlay': 'Cores do Overlay',
  'wx.cores_do_player': 'Cores do Player',
  'wx.cores_do_overlay_2': 'Cores do overlay',
  'wx.crie_sua_primeira_variacao': 'Crie sua primeira variação',
  'wx.curva_de_retencao': 'Curva de Retenção',
  'wx.custom': 'Custom',
  'wx.custom_icon': 'Custom icon',
  'wx.custom_overlay': 'Custom overlay',
  'wx.codigo_html': 'Código HTML',
  'wx.codigo_html_personalizado': 'Código HTML personalizado',
  'wx.codigo_copiado_para_a_area_de_transferencia': 'Código copiado para a área de transferência',
  'wx.codigo_copiado_para_a_area_de_transferencia_2': 'Código copiado para a área de transferência!',
  'wx.codigo_copiado': 'Código copiado!',
  'wx.dados_resetados_com_sucesso': 'Dados resetados com sucesso!',
  'wx.data_invalida': 'Data inválida',
  'wx.de': 'De',
  'wx.defina_uma_velocidade_fixa_ou_rode_um_teste_automatico_para_': 'Defina uma velocidade fixa ou rode um teste automático.',
  'wx.desabilitar_controles': 'Desabilitar controles',
  'wx.destaca_o_botao_central_de_play': 'Destaca o botão central de play',
  'wx.dica_clique_na_caixa_no_player_para_mover_e_redimensionar': 'Dica: clique na caixa no player para mover e redimensionar.',
  'wx.digite_o_novo_texto': 'Digite o novo texto',
  'wx.digite_o_texto_da_subheadline_opcional': 'Digite o texto da subheadline (opcional)',
  'wx.digite_o_texto_principal': 'Digite o texto principal...',
  'wx.digite_o_texto_secundario': 'Digite o texto secundário...',
  'wx.digite_um_nome_para_a_variacao': 'Digite um nome para a variação',
  'wx.direita': 'Direita',
  'wx.distribuicao_de_trafego_atualizada_com_sucesso': 'Distribuição de tráfego atualizada com sucesso!',
  'wx.duracao_da_exibicao_segundos': 'Duração da exibição (segundos)',
  'wx.duracao_do_teste': 'Duração do Teste',
  'wx.editar': 'Editar',
  'wx.em_execucao': 'Em execução',
  'wx.encerra_o_teste_e_mantem_a_variacao_vencedora_ativa': 'Encerra o teste e mantém a variação vencedora ativa.',
  'wx.engajamento': 'Engajamento',
  'wx.enviar_imagem': 'Enviar Imagem',
  'wx.enviar_icone_svg_recomendado': 'Enviar Ícone (SVG recomendado)',
  'wx.envie_uma_imagem_para_o_template_personalizado': 'Envie uma imagem para o template personalizado',
  'wx.escolha_o_estilo_visual_do_botao_de_play_do_seu_player': 'Escolha o estilo visual do botão de play do seu player.',
  'wx.escolha_o_icone': 'Escolha o ícone',
  'wx.escolha_um_modelo_visual_para_o_overlay': 'Escolha um modelo visual para o overlay',
  'wx.escolha_um_video_da_biblioteca': 'Escolha um vídeo da biblioteca',
  'wx.esconder_inicio': 'Esconder Início',
  'wx.esquerda': 'Esquerda',
  'wx.estilo': 'Estilo',
  'wx.estilo_avancado': 'Estilo Avançado',
  'wx.estilo_da_barra': 'Estilo da Barra',
  'wx.estilo_da_headline': 'Estilo da Headline',
  'wx.estilo_da_subheadline': 'Estilo da Subheadline',
  'wx.estilo_do_autoplay': 'Estilo do Autoplay',
  'wx.estilo_do_icone': 'Estilo do Ícone',
  'wx.estilo_e_comportamento': 'Estilo e Comportamento',
  'wx.evite_conclusoes_precipitadas_com_poucos_dados_espere_atingi': 'Evite conclusões precipitadas com poucos dados - espere atingir os valores mínimos recomendados.',
  'wx.ex_ative_o_som_para_melhor_experiencia': 'Ex.: Ative o som para melhor experiência',
  'wx.ex_seu_video_ja_comecou': 'Ex.: Seu vídeo já começou',
  'wx.ex_assista_ate_o_final_para_descobrir_o_metodo_que_esta_gera': 'Ex: Assista até o final para descobrir o método que está gerando vendas todos os dias',
  'wx.ex_autoplay_moderno_conversao_limpa': 'Ex: Autoplay Moderno, Conversão Limpa...',
  'wx.ex_botao_de_checkout': 'Ex: Botão de Checkout',
  'wx.ex_headline_moderna_conversao_limpa': 'Ex: Headline Moderna, Conversão Limpa...',
  'wx.ex_mostrar_botao_de_compra': 'Ex: Mostrar botão de compra',
  'wx.ex_seu_video_ja_comecou_2': 'Ex: Seu vídeo já começou',
  'wx.ex_toque_para_ativar_o_som': 'Ex: Toque para ativar o som',
  'wx.ex_ativo_visivel_destaque': 'Ex: ativo, visivel, destaque',
  'wx.ex_color_background_display': 'Ex: color, background, display',
  'wx.excluir': 'Excluir',
  'wx.excluir_variacao': 'Excluir variação?',
  'wx.exibe_modal_para_retomar_reproducao_de_onde_o_usuario_parou': 'Retoma o vídeo de onde o usuário parou.',
  'wx.exibe_uma_imagem_estatica_no_lugar_do_player': 'Exibe uma imagem estática no lugar do player',
  'wx.exibe_uma_mensagem_sutil_informando_que_o_video_iniciou_auto': 'Avisa que o vídeo iniciou automaticamente.',
  'wx.exibir_video_original': 'Exibir Vídeo Original',
  'wx.exibir_barra_de_controles': 'Exibir barra de controles',
  'wx.exibicao_por_dispositivo': 'Exibição por Dispositivo',
  'wx.experiencia_imersiva': 'Experiência imersiva',
  'wx.facilite_o_controle_de_tempo_durante_a_reproducao': 'Facilite o controle de tempo durante a reprodução.',
  'wx.familia_da_fonte': 'Família da Fonte',
  'wx.finalizar_teste_a_b': 'Finalizar teste A/B?',
  'wx.forma_do_botao': 'Forma do Botão',
  'wx.formato_da_imagem': 'Formato da Imagem',
  'wx.fundo': 'Fundo',
  'wx.fundo_botao': 'Fundo Botão',
  'wx.fundo_da_headline': 'Fundo da Headline',
  'wx.futuras_integracoes': 'Futuras Integrações',
  'wx.gratis_segredo_exclusivo_novo': 'Grátis, Segredo, Exclusivo, Novo',
  'wx.html_personalizado': 'HTML Personalizado',
  'wx.habilita_a_sincronizacao_inteligente_de_acoes_com_o_video': 'Habilita a sincronização inteligente de ações com o vídeo',
  'wx.habilita_o_botao_de_acao_no_video_selecionado': 'Habilita o botão de ação no vídeo selecionado',
  'wx.headline': 'Headline',
  'wx.headline_principal': 'Headline Principal',
  'wx.headline_nao_ativada': 'Headline não ativada',
  'wx.headline_por_dispositivo': 'Headline por Dispositivo',
  'wx.herdando_valores_do_desktop_altere_para_personalizar': 'Herdando valores do Desktop. Altere para personalizar.',
  'wx.hover_fundo': 'Hover Fundo',
  'wx.hover_texto': 'Hover Texto',
  'wx.ideal_para_videos_corporativos_e_educacionais': 'Ideal para vídeos corporativos e educacionais',
  'wx.imagem': 'Imagem',
  'wx.imagem_desktop': 'Imagem Desktop',
  'wx.imagem_mobile': 'Imagem Mobile',
  'wx.imagem_carregada_com_sucesso': 'Imagem carregada com sucesso!',
  'wx.imagem_de_fundo': 'Imagem de Fundo',
  'wx.imagem_do_autoplay': 'Imagem do Autoplay',
  'wx.imagem_do_continuar_de_onde_parou': 'Imagem do Continuar de onde parou',
  'wx.imagem_removida': 'Imagem removida',
  'wx.inferior_direito': 'Inferior Direito',
  'wx.inferior_esquerdo': 'Inferior Esquerdo',
  'wx.inicia_o_video_automaticamente_ao_entrar_na_pagina': 'Inicia o vídeo automaticamente ao entrar na página.',
  'wx.iniciar_sem_som': 'Iniciar sem som',
  'wx.interacao_com_video': 'Interação com Vídeo',
  'wx.inicio': 'Início',
  'wx.inicio_mm_ss': 'Início (mm:ss)',
  'wx.link_de_destino': 'Link de destino',
  'wx.link_do_botao': 'Link do Botão',
  'wx.loop_continuo': 'Loop contínuo',
  'wx.mantem_o_video_padrao_neste_dispositivo': 'Mantém o vídeo padrão neste dispositivo',
  'wx.melhor_para_captar_atencao_e_criar_urgencia': 'Melhor para captar atenção e criar urgência',
  'wx.melhor_plataforma': 'Melhor plataforma',
  'wx.mensagem': 'Mensagem',
  'wx.mensagem_personalizada': 'Mensagem personalizada',
  'wx.meu_titulo_personalizado': 'Meu título personalizado',
  'wx.modo_html': 'Modo HTML',
  'wx.modo_html_avancado': 'Modo HTML (Avançado)',
  'wx.modo_imagem': 'Modo Imagem',
  'wx.modo_texto': 'Modo Texto',
  'wx.modo_de_exibicao': 'Modo de Exibição',
  'wx.mostra_o_progresso_do_video': 'Mostra o progresso do vídeo',
  'wx.mostra_o_progresso_visual': 'Mostra o progresso visual',
  'wx.mostra_os_controles_na_parte_inferior_do_player': 'Mostra os controles na parte inferior do player',
  'wx.mostra_um_video_diferente_apenas_neste_dispositivo': 'Mostra um vídeo diferente apenas neste dispositivo',
  'wx.mostrar_imagem': 'Mostrar Imagem',
  'wx.mostrar_barra_de_progresso': 'Mostrar barra de progresso',
  'wx.mostrar_contagem_regressiva': 'Mostrar contagem regressiva',
  'wx.mostrar_controles': 'Mostrar controles',
  'wx.mostrar_controles_do_video_durante_o_autoplay': 'Mostrar controles do vídeo durante o autoplay',
  'wx.mostrar_elemento': 'Mostrar elemento',
  'wx.mostre_visualmente_o_avanco_do_video_e_aumente_a_retencao': 'Mostre o avanço do vídeo e aumente a retenção.',
  'wx.muito_rapido_ou_muito_lento': 'Muito rápido ou muito lento',
  'wx.maximo_8_10_palavras': 'Máximo 8-10 palavras',
  'wx.maximo_de_5_modelos_simultaneos_permitidos': 'Máximo de 5 modelos simultâneos permitidos',
  'wx.medio': 'Médio',
  'wx.metricas_rastreadas': 'Métricas Rastreadas:',
  'wx.metricas_atualizadas_para_novo_formato': 'Métricas atualizadas para novo formato!',
  'wx.minimo_2_variacoes': 'Mínimo: 2 variações',
  'wx.navegacao_do_video': 'Navegação do Vídeo',
  'wx.nenhum_teste_ativo': 'Nenhum teste ativo',
  'wx.nenhum_video_encontrado': 'Nenhum vídeo encontrado',
  'wx.nenhuma_variacao_configurada': 'Nenhuma variação configurada',
  'wx.nome_da_acao': 'Nome da Ação *',
  'wx.nome_da_classe': 'Nome da Classe',
  'wx.nome_da_variacao': 'Nome da Variação',
  'wx.novo_texto': 'Novo Texto',
  'wx.o_tempo_de_pitch_e': 'O tempo de Pitch é:',
  'wx.oculta_os_controles_ate_a_primeira_interacao': 'Oculta os controles até a primeira interação',
  'wx.ocultar_pause': 'Ocultar Pause',
  'wx.ocultar_video': 'Ocultar Vídeo',
  'wx.ocultar_elemento': 'Ocultar elemento',
  'wx.opacidade_do_fundo': 'Opacidade do fundo',
  'wx.open_sans': 'Open Sans',
  'wx.open_sans_classic': 'Open Sans (Classic)',
  'wx.ordem_atualizada': 'Ordem atualizada',
  'wx.overlay_personalizado': 'Overlay personalizado',
  'wx.para_conteudos_educativos_priorize_o_engajamento_para_vendas': 'Para conteúdos educativos, priorize o engajamento; para vendas, priorize a taxa de conversão',
  'wx.para_usar_a_barra_de_progresso_personalizada_desative_os_con': 'Para usar a Barra de Progresso personalizada, desative os controles do player.',
  'wx.percentual_de_visitantes_que_iniciaram_o_video': 'Percentual de visitantes que iniciaram o vídeo',
  'wx.percentual_medio_de_retencao_do_video_assistido': 'Percentual médio de retenção do vídeo assistido',
  'wx.perfeito_para_vendas_e_call_to_actions': 'Perfeito para vendas e call-to-actions',
  'wx.permita_interacao_com_o_video_e_aumente_controle_retencao_e_': 'Ative os controles do player para o espectador.',
  'wx.permite_ajustar_o_audio': 'Permite ajustar o áudio',
  'wx.permite_que_o_espectador_retome_o_video_de_onde_parou': 'Permite que o espectador retome o vídeo de onde parou.',
  'wx.personalizado': 'Personalizado',
  'wx.personalizacao_visual': 'Personalização Visual',
  'wx.personalize_a_aparencia_do_autoplay': 'Personalize a aparência do Autoplay',
  'wx.personalize_cores_dos_controles_e_da_barra_inferior': 'Personalize cores dos controles e da barra inferior.',
  'wx.personalize_cores_animacoes_e_efeitos_visuais': 'Personalize cores, animações e efeitos visuais',
  'wx.personalize_o_comportamento_do_player_para_cada_tipo_de_disp': 'Ajuste o player para cada tipo de dispositivo.',
  'wx.player_simulado': 'Player Simulado',
  'wx.plays_unicos': 'Plays Únicos',
  'wx.plays_unicos_por_usuario': 'Plays únicos por usuário',
  'wx.posicao': 'Posição',
  'wx.posicao_h': 'Posição H (%)',
  'wx.posicao_v': 'Posição V (%)',
  'wx.posicao_do_botao_no_video': 'Posição do botão no vídeo',
  'wx.posicao_e_tamanho': 'Posição e Tamanho',
  'wx.preencha_headline_ou_subheadline': 'Preencha Headline ou Subheadline.',
  'wx.preencha_todos_os_campos_obrigatorios': 'Preencha todos os campos obrigatórios',
  'wx.preview': 'Preview',
  'wx.preview_configurado': 'Preview configurado:',
  'wx.preview_de_frames_na_timeline': 'Preview de frames na timeline',
  'wx.preview_disponivel_no_player_real': 'Preview disponível no player real',
  'wx.preview_do_botao': 'Preview do Botão',
  'wx.preview_do_player': 'Preview do Player',
  'wx.progresso_do_teste': 'Progresso do Teste',
  'wx.pre_visualizacoes_de_estilos': 'Pré-Visualizações de Estilos',
  'wx.pre_visualizacao_ilustrativa': 'Pré-visualização ilustrativa',
  'wx.pre_visualizacao_ilustrativa_da_barra_de_progresso': 'Pré-visualização ilustrativa da barra de progresso',
  'wx.pre_visualizacao_ilustrativa_do_html_personalizado': 'Pré-visualização ilustrativa do HTML personalizado',
  'wx.pre_visualizacao_ilustrativa_do_botao_no_video': 'Pré-visualização ilustrativa do botão no vídeo',
  'wx.previa_do_botao': 'Prévia do Botão',
  'wx.pulsar': 'Pulsar',
  'wx.quando_a_headline_aparece': 'Quando a headline aparece',
  'wx.quando_a_headline_desaparece': 'Quando a headline desaparece',
  'wx.quando_ativado_o_video_reinicia_do_inicio_ao_clicar': 'Quando ativado, o vídeo reinicia do início ao clicar',
  'wx.realize_testes_com_pelo_menos_100_visualizacoes_por_velocida': 'Realize testes com pelo menos 100 visualizações por velocidade',
  'wx.recomenda_se_um_minimo_de_800_plays_unicos_por_variacao_para': 'Recomenda-se um mínimo de 800 plays únicos por variação para resultados estatisticamente significativos',
  'wx.recomendado': 'Recomendado',
  'wx.recomecar_video_apos_o_fim': 'Recomeçar vídeo após o fim',
  'wx.reiniciar_video': 'Reiniciar vídeo',
  'wx.remove_completamente_o_player_neste_dispositivo': 'Remove completamente o player neste dispositivo',
  'wx.remove_distracao_visual': 'Remove distração visual',
  'wx.remover_imagem': 'Remover imagem',
  'wx.repetir_video_loop': 'Repetir vídeo (loop)',
  'wx.reproducao_automatica': 'Reprodução automática',
  'wx.retencao_no_pitch': 'Retenção no Pitch',
  'wx.retencao_no_pitch_2': 'Retenção no pitch',
  'wx.retomada_automatica': 'Retomada automática',
  'wx.retomar_reproducao': 'Retomar reprodução',
  'wx.retangulo': 'Retângulo',
  'wx.selecione_a_posicao': 'Selecione a posição',
  'wx.selecione_a_velocidade_desejada': 'Selecione a velocidade desejada',
  'wx.selecione_o_tamanho': 'Selecione o tamanho',
  'wx.selecione_pelo_menos_2_modelos_para_iniciar_o_teste': 'Selecione pelo menos 2 modelos para iniciar o teste',
  'wx.selecione_uma_animacao': 'Selecione uma animação',
  'wx.sem_animacao': 'Sem animação',
  'wx.seu_html_aparecera_aqui': 'Seu HTML aparecerá aqui',
  'wx.seu_html_aqui': 'Seu HTML aqui',
  'wx.seu_video_ja_comecou': 'Seu vídeo já começou',
  'wx.slide_de_baixo': 'Slide de baixo',
  'wx.sobre_a_reproducao_automatica': 'Sobre a reprodução automática:',
  'wx.som_de_alerta': 'Som de alerta',
  'wx.status_do_teste': 'Status do Teste',
  'wx.substituir_por_outro_video': 'Substituir por Outro Vídeo',
  'wx.solido': 'Sólido',
  'wx.tamanho': 'Tamanho',
  'wx.tamanho_da_fonte': 'Tamanho da Fonte',
  'wx.tamanho_do_icone': 'Tamanho do Ícone',
  'wx.tamanho_e_posicao': 'Tamanho e Posição',
  'wx.tamanho_recomendado_1920x1080px_formatos_jpg_png_gif_maximo_': 'Tamanho recomendado: 1920x1080px • Formatos: JPG, PNG, GIF • Máximo: 10MB',
  'wx.taxa_de_conversao': 'Taxa de Conversão',
  'wx.tempo': 'Tempo',
  'wx.tempo_atual': 'Tempo Atual',
  'wx.tempo_atual_2': 'Tempo atual',
  'wx.tempo_ate_retomar': 'Tempo até retomar',
  'wx.tempo_de_exibicao': 'Tempo de Exibição',
  'wx.tempo_do_video_mm_ss': 'Tempo do Vídeo (mm:ss) *',
  'wx.tempo_para_exibicao_segundos': 'Tempo para exibição (segundos)',
  'wx.temporizacao': 'Temporização',
  'wx.teste_a_b_ativado': 'Teste A/B ativado!',
  'wx.teste_aplicado_com_sucesso': 'Teste aplicado com sucesso!',
  'wx.teste_finalizado': 'Teste finalizado',
  'wx.teste_finalizado_com_sucesso': 'Teste finalizado com sucesso!',
  'wx.texto': 'Texto',
  'wx.texto_botao': 'Texto Botão',
  'wx.texto_inferior': 'Texto Inferior',
  'wx.texto_superior': 'Texto Superior',
  'wx.texto_branco_em_fundo_claro': 'Texto branco em fundo claro',
  'wx.texto_da_headline': 'Texto da Headline',
  'wx.texto_da_subheadline': 'Texto da Subheadline',
  'wx.texto_do_botao': 'Texto do Botão',
  'wx.texto_do_botao_principal': 'Texto do botão principal',
  'wx.texto_do_link_secundario': 'Texto do link secundário',
  'wx.texto_exibido_no_overlay_para_orientar_o_usuario': 'Texto exibido no overlay para orientar o usuário',
  'wx.textos_do_overlay': 'Textos do overlay',
  'wx.ticket_medio': 'Ticket médio',
  'wx.tipo': 'Tipo',
  'wx.tipo_de_animacao': 'Tipo de Animação',
  'wx.tipo_de_acao': 'Tipo de Ação *',
  'wx.tipo_de_fundo': 'Tipo de Fundo',
  'wx.tipo_de_som': 'Tipo de som',
  'wx.tipo_2': 'Tipo:',
  'wx.todos_os_videos': 'Todos os vídeos',
  'wx.topo_direita': 'Topo Direita',
  'wx.topo_esquerda': 'Topo Esquerda',
  'wx.topo_pode_funcionar_melhor_que_rodape': 'Topo pode funcionar melhor que rodapé',
  'wx.total_de_visualizacoes_do_video': 'Total de visualizações do vídeo',
  'wx.trafego': 'Tráfego',
  'wx.trafego_dividido_igualmente': 'Tráfego dividido igualmente',
  'wx.termino': 'Término',
  'wx.termino_previsto': 'Término previsto:',
  'wx.titulo': 'Título',
  'wx.titulo_opcional': 'Título (opcional)',
  'wx.titulo_do_overlay': 'Título do overlay',
  'wx.uma_mensagem_principal_por_vez': 'Uma mensagem principal por vez',
  'wx.upload_do_icone_personalizado': 'Upload do Ícone Personalizado',
  'wx.usar_cor_do_play': 'Usar cor do Play',
  'wx.usar_velocidade_fixa': 'Usar velocidade fixa',
  'wx.use_id_para_ids_ou_classe_para_classes_css_exemplo_meubotao_': 'Use #id para IDs ou .classe para classes CSS. Exemplo: #meuBotao ou .minha-secao',
  'wx.use_o_calendario_para_comparar_periodos_semelhantes': 'Use o calendário para comparar períodos semelhantes',
  'wx.variacao': 'Variação',
  'wx.variacao_adicionada': 'Variação adicionada!',
  'wx.variacao_atualizada': 'Variação atualizada!',
  'wx.variacao_excluida': 'Variação excluída',
  'wx.variacao_removida': 'Variação removida!',
  'wx.variacao_salva': 'Variação salva!',
  'wx.variacoes': 'Variações',
  'wx.velocidade': 'Velocidade',
  'wx.velocidade_fixa': 'Velocidade Fixa',
  'wx.velocidade_atual': 'Velocidade atual',
  'wx.velocidade_final': 'Velocidade final',
  'wx.velocidade_inicial': 'Velocidade inicial',
  'wx.vis_unicas': 'Vis. Únicas',
  'wx.visualizacoes': 'Visualizações',
  'wx.visualizacoes_unicas': 'Visualizações Únicas',
  'wx.visualizacoes_unicas_por_usuario': 'Visualizações únicas (por usuário)',
  'wx.voce_parou_em_um_ponto_importante_do_video': 'Você parou em um ponto importante do vídeo.',
  'wx.volte_para_revisar_momentos': 'Volte para revisar momentos',
  'wx.video_de_fundo_inteligente': 'Vídeo de Fundo Inteligente',
  'wx.ate_2': 'até',
  'wx.https_exemplo_com': 'https://exemplo.com',
  'wx.https_exemplo_com_br': 'https://exemplo.com.br',
  'wx.segundos': 'segundos',
  'wx.icone': 'Ícone',
  'wx.icone_personalizado': 'Ícone Personalizado',
  'wx.icone_do_play': 'Ícone do Play',
  'wx.analytics_avancados': '• Analytics avançados',
  'wx.animacoes_css': '• Animações CSS',
  'wx.ate_3_variacoes_simultaneas': '• Até 3 variações simultâneas',
  'wx.ctr_click_through_rate': '• CTR (Click-through Rate)',
  'wx.compressao_automatica': '• Compressão automática',
  'wx.configuracoes_de_qualidade': '• Configurações de qualidade',
  'wx.controle_total_com_codigo': '• Controle total com código',
  'wx.cores_personalizaveis': '• Cores personalizáveis',
  'wx.cores_que_nao_contrastam': '• Cores que não contrastam:',
  'wx.crie_urgencia': '• Crie urgência:',
  'wx.codigo_de_embed': '• Código de embed',
  'wx.divisao_automatica_de_trafego': '• Divisão automática de tráfego',
  'wx.editor_visual_intuitivo': '• Editor visual intuitivo',
  'wx.engajamento_2': '• Engajamento',
  'wx.headlines_genericas': '• Headlines genéricas:',
  'wx.integracao_com_cdn': '• Integração com CDN',
  'wx.integracao_com_js': '• Integração com JS',
  'wx.mantenha_legivel': '• Mantenha legível:',
  'wx.metricas_em_tempo_real': '• Métricas em tempo real',
  'wx.multiplos_focos': '• Múltiplos focos:',
  'wx.otimizacao_de_performance': '• Otimização de performance',
  'wx.para_desenvolvedores': '• Para desenvolvedores',
  'wx.posicao_que_cobre_conteudo': '• Posição que cobre conteúdo:',
  'wx.preview_em_tempo_real': '• Preview em tempo real',
  'wx.seja_especifico': '• Seja específico:',
  'wx.tamanho_maximo_2mb': '• Tamanho máximo: 2MB',
  'wx.taxa_de_conversao_2': '• Taxa de conversão',
  'wx.tempo_medio_de_exibicao': '• Tempo médio de exibição',
  'wx.teste_posicoes': '• Teste posições:',
  'wx.texto_muito_longo': '• Texto muito longo:',
  'wx.upload_de_imagens_gifs': '• Upload de imagens/GIFs',
  'wx.use_numeros': '• Use números:',
  'wx.versoes_desktop_mobile': '• Versões desktop/mobile',
  'wx.alguns_navegadores_podem_restringir_o_audio_automatico_mesmo': '⚠️ Alguns navegadores podem restringir o áudio automático mesmo após interação do visitante.',
  'wx.velocidades_muito_altas_acima_de_1_5x_podem_prejudicar_a_com': '⚠️ Velocidades muito altas (acima de 1.5x) podem prejudicar a compreensão e confiança no conteúdo.',
  'wx.requisito_autoplay_ativo': '⚡ Requisito: Autoplay Ativo',
  'wx.faca': '✅ Faça',
  'wx.modos_de_criacao': '🎨 Modos de Criação',
  'wx.melhores_praticas': '🎯 Melhores Práticas',
  'wx.dicas_de_templates': '💡 Dicas de Templates',

  'wx2.fontWeight': 'Peso da Fonte',
  'wx2.all': 'Todas',
  'wx2.none': 'Nenhuma',
  'wx2.fadeIn': 'Fade In',
  'wx2.fadeInHyphen': 'Fade-in',
  'wx2.loadBlock': 'Carregar bloco',
  'wx2.status': 'Status',
  'wx2.statusColon': 'Status:',
  'wx2.upload': 'Upload',
  'wx2.autoplayMessages': 'Mensagens do Autoplay',
  'wx2.showBorder': 'Exibir borda',
  'wx2.saveProgressAuto': 'Salva o progresso do espectador automaticamente',
  'wx2.continueWatching': 'Continue Assistindo',
  'wx2.pictureInPicture': 'Picture-in-Picture',
  'wx2.initialCounterValue': 'Valor inicial do contador',
};

const en: Dict = {
  'panel.activateToConfigure': 'Enable the feature to configure',

  'ab.title': 'Action Button',
  'ab.desc': 'Guide your viewers with a CTA at the right moment.',
  'ab.config': 'Button Setup',
  'ab.config.desc': 'Set the text and link for your CTA.',
  'ab.text': 'Button text',
  'ab.link': 'Button link',
  'ab.appearance': 'Appearance',
  'ab.appearance.desc': 'Customize the button colors.',
  'ab.bg': 'Background',
  'ab.fontColor': 'Text',
  'ab.colorButton': 'Button color',
  'ab.colorText': 'Text color',
  'ab.showTime': 'Display time',
  'ab.showTime.desc': 'Choose when the button shows up in the video.',
  'ab.seconds': 'seconds',

  'sn.enable': 'Sales Notification',
  'sn.enable.helper': 'Show recent sales to build social proof.',
  'sn.tab.basic': 'Settings',
  'sn.tab.content': 'Content',
  'sn.tab.appearance': 'Appearance',
  'sn.activeStatusTpl': 'Active · 1 notification every {freq}s · {pos}',
  'sn.speed.title': 'Notification Speed',
  'sn.speed.startDelay': 'Initial delay',
  'sn.speed.frequency': 'Frequency',
  'sn.speed.duration': 'Duration',
  'sn.position.title': 'Position',
  'sn.pos.top-left': 'Top Left',
  'sn.pos.top-right': 'Top Right',
  'sn.pos.bottom-left': 'Bottom Left',
  'sn.pos.bottom-right': 'Bottom Right',
  'sn.loop.title': 'Repeat in loop',
  'sn.loop.desc': 'Restarts automatically after showing all.',
  'sn.interaction.title': 'Interaction',
  'sn.interaction.openLink': 'On click, open link',
  'sn.interaction.openLinkDesc': 'Opens a URL when the notification is clicked.',
  'sn.interaction.urlPlaceholder': 'https://example.com/offer',
  'sn.interaction.urlHelp': 'On click, open this URL in a new tab.',
  'sn.interaction.urlInvalid': 'Invalid URL. Use http:// or https://. e.g. https://your-site.com',
  'sn.interaction.urlAutofix': 'No http:// — it will be saved as https://{url}',
  'sn.sound.title': 'Sound',
  'sn.sound.desc': 'Plays a sound together with the notification.',
  'sn.sound.volume': 'Volume',
  'sn.sound.test': 'Test sound',
  'sn.message.title': 'Message',
  'sn.message.product': 'Product name',
  'sn.message.productPlaceholder': 'e.g. Premium Course',
  'sn.message.showPlatform': 'Show platform',
  'sn.message.selectPlatform': 'Select a platform',
  'sn.message.platformCustom': 'Custom',
  'sn.message.customLogo': 'Custom logo',
  'sn.message.changeLogo': 'Click to replace',
  'sn.message.addLogo': 'Add logo',
  'sn.simulated.title': 'Simulated data',
  'sn.simulated.names': 'Names',
  'sn.simulated.cities': 'Cities',
  'sn.simulated.auto': 'Automatic suggestion',
  'sn.simulated.recommended': '(recommended)',
  'sn.simulated.custom': 'Custom list',
  'sn.simulated.noLocation': 'Hide location',
  'sn.simulated.typeEnter': 'Type and press Enter',
  'sn.simulated.add10': 'Add 10 examples',
  'sn.template.title': 'Template text',
  'sn.template.placeholder': '{name} from {city} just bought {product}',
  'sn.template.insert': 'Insert:',
  'sn.template.preview': 'Preview',
  'sn.appearance.title': 'Style',
  'sn.appearance.bg': 'Background color',
  'sn.appearance.border': 'Border color',
  'sn.appearance.color': 'Text color',
  'sn.appearance.radius': 'Corner radius',
  'sn.appearance.showIcon': 'Show icon',
  'sn.quick.title': 'Quick tweaks',
  'sn.quick.size': 'Size',
  'sn.size.small': 'Small',
  'sn.size.medium': 'Medium',
  'sn.size.large': 'Large',
  'sn.disclaimer': 'These settings do not affect how the video works.',
  'sn.colorCustom': 'Custom color',
  'sn.addMore': 'Add...',
  'sn.fallbackPos': 'Bottom Right',

  'border.title': 'Player Border',
  'border.desc': 'Highlight the player with border and shadow.',
  'border.appearance': 'Appearance',
  'border.appearance.desc': 'Border color, thickness and corner radius.',
  'border.color': 'Border color',
  'border.width': 'Thickness',
  'border.radius': 'Corner radius',
  'border.clickToChange': 'Click to change',
  'border.shadow.toggle': 'Enable shadow',
  'border.shadow.toggleDesc': 'Add depth and emphasis to the player.',
  'border.shadow.title': 'Shadow Settings',
  'border.shadow.desc': 'Adjust color, blur and intensity.',
  'border.shadow.color': 'Shadow color',
  'border.shadow.blur': 'Blur',
  'border.shadow.intensity': 'Intensity',

  'controls.title': 'Video Controls',
  'controls.desc': 'Configure the video playback behavior.',
  'controls.loopBehavior': 'Loop Behavior',
  'controls.loopBehavior.desc': 'Whether the video repeats automatically.',
  'controls.noRepeat': 'Do not repeat',
  'controls.repeat': 'Repeat',

  'ap.title': 'Smart Pause',
  'ap.desc': 'Pauses the video when the viewer leaves the tab.',
  'ap.helper': 'Works when the visitor switches tabs or minimizes the browser.',
  'ap.on': 'Smart pause enabled',
  'ap.off': 'Smart pause disabled',
  'ap.active': 'Active',
  'ap.onDesc': 'The video pauses when leaving the tab and resumes automatically on return.',
  'ap.offDesc': 'Enable the feature above to pause the video when the visitor leaves the tab.',

  'rae.title': 'Restart video on end',
  'rae.desc': 'Restarts the video when playback ends.',
  'rae.helper': 'Works at the end of video playback.',
  'rae.on': 'Auto restart enabled',
  'rae.off': 'Auto restart disabled',
  'rae.onDesc': 'When finished, the video returns to the start automatically, keeping playback on loop.',
  'rae.offDesc': 'Enable the feature above to restart the video automatically when it reaches the end.',

  'aff.title': 'Enable affiliate link',
  'aff.desc': 'Shows the HostVSL logo and enables your affiliate link.',
  'aff.linkLabel': 'Your affiliate link',
  'aff.linkPlaceholder': 'https://hostvsl.com/?ref=YOUR_ID',
  'aff.linkInvalid': 'This link doesn\'t look valid. Please check and try again.',
  'aff.linkHelper': 'Enter your affiliate link to earn commissions. The HostVSL logo will appear in the top-right corner of the player and clicking it opens your affiliate link in a new tab.',
  'aff.previewTitle': 'HostVSL logo will be shown on the player.',
  'aff.previewClick': 'User click will open:',

  'pix.tokenCopied': 'Token copied!',
  'pix.facebook': 'Facebook',

  'cw.title': 'Continue from where you left off',
  'cw.desc': 'Lets users resume the video from where they stopped',
  'cw.tab.templates': 'Templates',
  'cw.tab.customize': 'Customization',
  'cw.templates.helper': 'Pick a template for the resume screen',
  'cw.templates.resume': 'Resume',
  'cw.upload.title': 'Custom Upload',
  'cw.upload.subtitle': 'Drag your file or click to upload',
  'cw.upload.maxSize': 'Max size: 2MB',
  'cw.upload.swap': 'Replace',
  'cw.upload.remove': 'Remove',
  'cw.customize.soon': 'Customization options coming soon.',

  'th.title': 'Video Thumbnails',
  'th.desc': 'Set strategic images to boost attention.',
  'th.subhint': 'Start, pause, and end can have different images.',
  'th.tab.initial': 'Start',
  'th.tab.pause': 'Pause',
  'th.tab.end': 'End',
  'th.toast.added.title': 'Thumbnail added successfully',
  'th.toast.added.initial': 'The start thumbnail has been set',
  'th.toast.added.pause': 'Configure the action button if you wish',
  'th.toast.added.end': 'The end thumbnail has been set',
  'th.toast.removed.title': 'Thumbnail removed successfully',
  'th.toast.removed.desc': 'The thumbnail has been removed',
  'th.toast.mode.title': 'Mode updated',
  'th.toast.mode.always': 'Thumbnail will show whenever the video pauses',
  'th.toast.mode.range': 'Thumbnail will show only in the specific range',
  'th.toast.button.added.title': 'Button added',
  'th.toast.button.added.desc': 'Configure the button and check the preview',
  'th.toast.button.removed.title': 'Button removed',
  'th.toast.button.removed.desc': 'The button was removed from the pause thumbnail',
  'th.button.defaultText': 'Keep Watching',

  'fc.title': 'Smart Final Countdown',
  'fc.desc': 'Shows a countdown at the end of the video and redirects automatically.',
  'fc.tab.settings': 'Settings',
  'fc.tab.style': 'Style',
  'fc.essential': 'Essential',
  'fc.timeLabel': 'Countdown time',
  'fc.redirect': 'Redirect',
  'fc.onEnd': 'When it ends',
  'fc.action.redirect': 'Redirect automatically when finished',
  'fc.action.warning': 'Just show the warning',
  'fc.urlLabel': 'Destination URL',
  'fc.urlPlaceholder': 'https://your-checkout.com',
  'fc.url.empty': 'Enter a URL to enable the redirect.',
  'fc.url.invalid': 'Invalid URL. e.g. https://your-site.com',
  'fc.url.autofix': 'No http:// — it will be saved as https://{url}',
  'fc.button.noTarget': 'Without a valid redirect URL the button is not shown to the viewer.',
  'fc.dismiss': 'Close and go back to the video',
  'fc.linkOpen': 'Link opening',
  'fc.openSame': 'Open in the same tab',
  'fc.openNew': 'Open in a new tab',
  'fc.warningOnly': 'These options are used only when automatic redirect is enabled.',
  'fc.cardContent': 'Card Content',
  'fc.cardTitle': 'Card title',
  'fc.cardTitlePlaceholder': 'Special Offer Unlocked!',
  'fc.cardDesc': 'Description',
  'fc.cardDescPlaceholder': 'You will be redirected in a few seconds…',
  'fc.cardDescHelp': 'The countdown will be displayed automatically based on the time set.',
  'fc.button': '"Go Now" Button',
  'fc.button.show': 'Show button',
  'fc.button.text': 'Button text',
  'fc.button.textPlaceholder': 'Go Now',
  'fc.button.action': 'Button action',
  'fc.appearance': 'Appearance',
  'fc.displayMode': 'Display mode',
  'fc.mode.overlay': 'Overlay on video',
  'fc.mode.fullscreen': 'Full screen',
  'fc.preset': 'Style preset',
  'fc.preset.host': 'Host Default',
  'fc.preset.host.sub': 'Recommended',
  'fc.preset.minimal': 'Subtle',
  'fc.preset.minimal.sub': 'Soft and clean',
  'fc.quick': 'Quick tweaks',
  'fc.quick.opacity': 'Background opacity',
  'fc.quick.radius': 'Card radius',
  'fc.quick.mainColor': 'Main color',
  'fc.quick.mainColorName': 'Main Color',
  'fc.quick.mainColorHelp': 'Applied to button and details',
  'fc.quick.bgColor': 'Background color',
  'fc.quick.customColor': 'Custom Color',
  'fc.count': 'Countdown',
  'fc.count.circle': 'Circle Color',
  'fc.count.number': 'Number Color',
  'fc.count.size': 'Size',
  'fc.size.s': 'S',
  'fc.size.m': 'M',
  'fc.size.l': 'L',
  'fc.typography': 'Typography',
  'fc.typo.title': 'Title Color',
  'fc.typo.desc': 'Description Color',
  'fc.btnGroup': 'Button',
  'fc.btn.color': 'Button Color',
  'fc.btn.text': 'Text Color',
  'fc.btn.radius': 'Radius',

  // Logo
  'lg.title': 'Logo',
  'lg.desc': 'Display your logo over the video.',
  'lg.tab.config': 'Settings',
  'lg.tab.appearance': 'Appearance',
  'lg.upload': 'Upload Logo',
  'lg.uploadError.format': 'Unsupported format. Use PNG or JPG.',
  'lg.uploadError.size': 'File larger than {n}MB',
  'lg.uploadError.generic': 'Error processing file',
  'lg.link': 'Destination link',
  'lg.link.placeholder': 'yoursite.com',
  'lg.link.invalid': 'Invalid URL',
  'lg.link.invalidDomain': 'Enter a valid domain. Ex: yoursite.com',
  'lg.link.savedAs': '→ Will be saved as:',
  'lg.link.helper': 'Accepts: yoursite.com, www.yoursite.com or https://yoursite.com',
  'lg.utm.enable': 'Add UTM parameters',
  'lg.utm.source': 'utm_source (e.g.: youtube)',
  'lg.utm.medium': 'utm_medium (e.g.: video)',
  'lg.utm.campaign': 'utm_campaign (e.g.: launch)',
  'lg.posSize': 'Position and Size',
  'lg.posLabel': 'Position in the player',
  'lg.fineTune': 'Fine adjustments',
  'lg.offsetX': 'Horizontal Offset',
  'lg.offsetY': 'Vertical Offset',
  'lg.sizeOpacity': 'Size and Transparency',
  'lg.size': 'Size',
  'lg.opacity': 'Transparency',
  'lg.border.add': 'Add border',
  'lg.border.color': 'Border color',
  'lg.border.width': 'Width',
  'lg.shadow.add': 'Add shadow',
  'lg.shadow.color': 'Shadow color',
  'lg.shadow.blur': 'Blur',
  'lg.shadow.intensity': 'Intensity',
  'lg.pos.top-left': 'Top Left',
  'lg.pos.top-center': 'Top Center',
  'lg.pos.top-right': 'Top Right',
  'lg.pos.center-left': 'Center Left',
  'lg.pos.center': 'Center',
  'lg.pos.center-right': 'Center Right',
  'lg.pos.bottom-left': 'Bottom Left',
  'lg.pos.bottom-center': 'Bottom Center',
  'lg.pos.bottom-right': 'Bottom Right',
  // Mini-Hook
  'mh.title': 'Mini-Hook',
  'mh.desc': 'Show images or GIFs at strategic moments.',
  'mh.image': 'Image',
  'mh.delete': 'Delete',
  'mh.empty.title': 'Start with your first Mini-Hook',
  'mh.empty.desc': 'Use Mini-Hooks to display images or GIFs at strategic moments. Ideal to reinforce promises, create visual cues, and recover attention.',
  'mh.empty.cta': 'Create first Mini-Hook',
  'mh.count.suffix': 'Mini-Hook',
  'mh.count.suffixPlural': 'Mini-Hooks',
  'mh.activeSuffix': 'active',
  'mh.activeSuffixPlural': 'active',
  'mh.new': 'New Mini-Hook',
  'mh.search': 'Search Mini-Hook',
  'mh.back': 'Back',
  'mh.name': 'Internal name',
  'mh.name.placeholder': 'E.g.: Reinforce initial promise',
  'mh.imageOrGif': 'Image or GIF',
  'mh.upload.cta': 'Click or drag the file here',
  'mh.upload.formats': 'JPG, PNG, WEBP and GIF • up to 20 MB',
  'mh.file.default': 'File',
  'mh.file.gif': 'Animated GIF',
  'mh.file.image': 'Static image',
  'mh.file.remove': 'Remove',
  'mh.file.swap': 'Swap',
  'mh.opt.analyzing': 'Analyzing…',
  'mh.opt.optimizing': 'Optimizing…',
  'mh.opt.light': 'Lightweight file',
  'mh.opt.heavy': 'May impact performance',
  'mh.opt.heavyGif': 'This GIF is heavy ({size}) and may impact preview fluidity. We recommend using a lighter GIF or reducing dimensions.',
  'mh.opt.auto': 'Auto-optimize',
  'mh.opt.autoDesc': 'Optimized GIFs make the Mini-Hook smoother.',
  'mh.timing': 'Timing',
  'mh.timing.start': 'Appear at',
  'mh.timing.end': 'Disappear at',
  'mh.opacity': 'Opacity',
  'mh.cancel': 'Cancel',
  'mh.save': 'Save changes',
  'mh.create': 'Create Mini-Hook',
  'mh.toast.tooLarge': 'File too large. Max: 20 MB',
  'mh.toast.invalidFormat': 'Invalid format. Use JPG, PNG, WEBP or GIF.',
  'mh.toast.optimized': 'GIF optimized • {n}% lighter',
  'mh.toast.heavyWarn': 'Heavy GIF may impact preview fluidity.',
  'mh.toast.updated': 'Mini-Hook updated successfully',
  'mh.toast.created': 'Mini-Hook created successfully',
  'mh.toast.duplicated': 'Mini-Hook duplicated successfully',
  'mh.toast.removed': 'Mini-Hook removed successfully',
  'mh.toast.overlap': 'This Mini-Hook overlaps with "{name}" in the same interval.',
  'mh.copy.suffix': '(copy)',
  'mh.validate.upload': 'Upload an image or GIF to continue.',
  'mh.validate.name': 'Enter a name to identify this Mini-Hook.',
  'mh.validate.endGtStart': 'End time must be greater than start time.',
  'mh.confirm.title': 'Delete Mini-Hook?',
  'mh.confirm.desc': 'Are you sure you want to delete this Mini-Hook? This action cannot be undone.',
  'mh.confirm.cancel': 'Cancel',
  'mh.confirm.delete': 'Delete',
  // Subtitles
  'sb.title': 'Subtitles',
  'sb.desc': 'Add subtitles to improve accessibility.',
  'sb.empty.title': 'Set up your subtitles',
  'sb.empty.desc': 'With subtitles enabled, you can add .SRT or .VTT files to your video. Viewers can toggle them via the CC button on the controls.',
  'sb.empty.hint': 'Turn on the switch above to start configuring your subtitles.',
  'sb.help': 'Learn about subtitles',
  'sb.help.tip': 'Upload a .VTT or .SRT file with synchronized subtitles. Both formats are supported.',
  'sb.file.title': 'Subtitle file',
  'sb.file.desc': 'Upload the .VTT or .SRT file with synchronized subtitles.',
  'sb.upload.cta': 'Click or drag the .VTT or .SRT file',
  'sb.upload.formats': 'Accepted formats: .VTT, .SRT',
  'sb.upload.error': 'Invalid or corrupted file. No subtitles found.',
  'sb.upload.template': 'Download the template file to see the correct format',
  'sb.help.section': 'Need help creating your subtitles?',
  'sb.help.download': 'Download template (.TXT)',
  'sb.help.downloadDesc': 'Download a simple example to fill in your subtitles.',
  'sb.help.convert': 'Convert to VTT',
  'sb.help.convertDesc': 'Convert your text to the accepted format.',
  'sb.file.loaded': 'Loaded',
  'sb.file.cuesOne': 'subtitle',
  'sb.file.cuesMany': 'subtitles',
  'sb.file.edit': 'Edit subtitle',
  'sb.file.replace': 'Replace file',
  'sb.file.remove': 'Remove subtitle',
  'sb.preview.title': 'Preview',
  'sb.preview.search': 'Search subtitle...',
  'sb.preview.empty': 'No subtitles found for "{q}"',
  'sb.preview.showLess': 'Show less',
  'sb.preview.showAll': 'Show all ({n})',
  'sb.cue.editText': 'Edit text',
  'sb.cue.delete': 'Delete subtitle',
  'sb.cue.editTime': 'Click to edit time',
  'sb.editor.title': 'Edit VTT content',
  'sb.editor.save': 'Save',
  'sb.editor.cancel': 'Cancel',
  'sb.toast.timeInvalid': 'Invalid time. Use m:ss.s format and ensure start < end.',
  'sb.toast.timeUpdated': 'Time updated!',
  'sb.toast.textEmpty': 'Subtitle text cannot be empty.',
  'sb.toast.textUpdated': 'Text updated!',
  'sb.toast.confirmDelete': 'Are you sure you want to delete this subtitle?',
  'sb.toast.lastRemoved': 'Last subtitle removed. File deleted.',
  'sb.toast.deleted': 'Subtitle deleted!',
  'sb.toast.reordered': 'Order updated!',
  'sb.settings.title': 'Subtitle settings',
  'sb.settings.desc': 'Customize how subtitles are displayed in the player.',
  'sb.settings.showByDefault': 'Show subtitles by default',
  'sb.settings.showByDefaultDesc': 'Subtitles start active when the video loads',
  'sb.settings.ccButton': 'Show subtitle button',
  'sb.settings.ccButtonDesc': 'Lets the viewer enable or disable',
  'sb.settings.warn': 'Subtitles will be disabled for the viewer.',
  'sb.settings.fontSize': 'Font size',
  'sb.settings.align': 'Alignment',
  'sb.settings.textColor': 'Text color',
  'sb.settings.bgColor': 'Background color',
  'sb.settings.bgOpacity': 'Background opacity',
  'sb.style.title': 'Subtitle style',
  'sb.style.desc': 'Choose how the subtitle appears in the player.',
  'sb.style.animType': 'Animation type',
  'sb.anim.none': 'Default',
  'sb.anim.noneDesc': 'No animation',
  'sb.anim.fade': 'Smooth fade',
  'sb.anim.fadeDesc': 'Smooth transition',
  'sb.anim.pop': 'Pop / Highlight',
  'sb.anim.popDesc': 'Scale with impact',
  'sb.anim.word': 'Word by word',
  'sb.anim.wordDesc': 'TikTok style',
  'sb.style.highlight': 'Highlight style',
  'sb.hl.background': 'Colored background',
  'sb.hl.textOnly': 'Text color',
  'sb.hl.scale': 'Light scale',
  'sb.hl.color': 'Highlight color',
  // === Auto-generated W4 sweep ===
  'wx.headlines_subheadlines_what': '📋 What are Headlines/Subheadlines?',
  'wx.confirm_delete_action': 'Do you really want to delete this action?',
  'wx.assista_esse_video': '"Watch this video"',
  'wx.ganhe_r_1_000_mes_vs_ganhe_dinheiro': '"Earn $1,000/month" vs "Make money"',
  'wx.ultimas_24h_vagas_limitadas': '"Last 24h", "Limited Spots"',
  'wx.div_seu_html_aqui_div': '<div>Your HTML here</div>',
  'wx.h1_meu_titulo_personalizado_h1_10_cole_seu_codigo_html_aqui': '<h1>My custom title</h1>&#10;Paste your HTML code here...',
  'wx.a_distribuicao_precisa_somar_100': 'Distribution must total 100%',
  'wx.a_reproducao_automatica_cria_a_sensacao_de_inicio_imediato_a': 'Autoplay creates a sense of immediate start, boosting user engagement. The video begins muted to comply with browser policies, and the overlay encourages audio activation.',
  'wx.a_soma_da_distribuicao_de_trafego_deve_ser_100': 'Traffic distribution must sum to 100%',
  'wx.adicionar_classe': 'Add class',
  'wx.aguarde_pelo_menos_7_dias_para_que_o_teste_colete_dados_sufi': 'Wait at least 7 days for the test to collect enough data',
  'wx.ajustamos_o_intervalo_para_manter_a_ordem_correta': 'We adjusted the interval to keep the correct order.',
  'wx.ajuste_a_aparencia_do_overlay_de_autoplay': 'Adjust the autoplay overlay appearance',
  'wx.ajuste_velocidade_e_visibilidade_dos_controles': 'Adjust speed and visibility of the controls.',
  'wx.ajustes_finos': 'Fine tuning',
  'wx.alterar_estilo': 'Change style',
  'wx.alterar_texto': 'Change text',
  'wx.animacao_pulsar': 'Pulse animation',
  'wx.animacao_de_entrada': 'Enter animation',
  'wx.ao_ativar_a_headline_sera_exibida_diretamente_sobre_o_video_': 'Shows the headline directly over the video.',
  'wx.ao_finalizar_a_velocidade_com_melhor_engajamento_sera_defini': 'When finished, the speed with the best engagement will be set as primary.',
  'wx.aparencia': 'Appearance',
  'wx.aparencia_do_player': 'Player appearance',
  'wx.aparencia_dos_controles': 'Controls appearance',
  'wx.aplicada_em_barra_de_controle_play_volume_e_botoes_principai': 'Applied to control bar, play, volume and main buttons',
  'wx.aplicada_em_icones_secundarios_e_textos_do_player': 'Applied to secondary icons and player texts',
  'wx.arraste_ou_clique_para_enviar': 'Drag or click to upload',
  'wx.arraste_seu_arquivo_ou_clique_para_enviar': 'Drag your file or click to upload',
  'wx.arraste_sua_imagem_de_fundo': 'Drag your background image',
  'wx.assistir_do_inicio': 'Watch from the start',
  'wx.ativar_autoplay': 'Enable Autoplay',
  'wx.ativar_acoes_sincronizadas': 'Enable Synced Actions',
  'wx.ativar_continuar_assistindo': 'Enable Continue Watching',
  'wx.ativar_headline': 'Enable Headline',
  'wx.ativar_velocidade': 'Enable Speed',
  'wx.ativar_botao': 'Enable button',
  'wx.ativar_tema_do_player': 'Enable player theme',
  'wx.ativar_teste_automatico': 'Enable auto test',
  'wx.ative_o_autoplay_na_aba_configuracao_para_configurar_testes_': 'Enable autoplay in the Settings tab to configure A/B tests',
  'wx.ative_o_autoplay_para_ver_o_preview': 'Enable autoplay to see the preview',
  'wx.ative_o_som': 'Enable sound',
  'wx.ate': 'To',
  'wx.aumente_a_retencao_com_um_video_gancho_no_autoplay': 'Boost retention with a hook video in autoplay',
  'wx.avalie_o_contexto_do_seu_conteudo_videos_tecnicos_podem_func': 'Consider your content context — technical videos may perform better at slower speeds',
  'wx.avancado': 'Advanced',
  'wx.avancar_10s': 'Forward 10s',
  'wx.acao': 'Action',
  'wx.acao_atualizada_com_sucesso': 'Action updated successfully!',
  'wx.acao_criada_com_sucesso': 'Action created successfully!',
  'wx.acao_excluida_com_sucesso': 'Action deleted successfully!',
  'wx.acoes': 'Actions',
  'wx.baixo_direita': 'Bottom Right',
  'wx.baixo_esquerda': 'Bottom Left',
  'wx.barra_inferior': 'Bottom Bar',
  'wx.barra_de_controles': 'Control Bar',
  'wx.barra_de_progresso': 'Progress Bar',
  'wx.botao_continuar': 'Continue button',
  'wx.botao_de_acao': 'Action Button',
  'wx.botao_discreto_sobre_o_video': 'Discreet button over the video',
  'wx.botao_play_pequeno': 'Small play button',
  'wx.botao_reiniciar': 'Restart button',
  'wx.buscar_videos': 'Search videos...',
  'wx.c_no_botao': 'Button color',
  'wx.c_no_botao_2': 'Button color',
  'wx.click': 'Click',
  'wx.click_here': 'Click Here',
  'wx.cole_seu_codigo_html_aqui_10_aceita_html_css_inline_e_script': 'Paste your HTML code here...&#10;Accepts HTML, inline CSS and lightweight scripts.',
  'wx.como_ficara_no_video': 'How it will look in the video',
  'wx.compare_no_minimo_3_velocidades_diferentes_para_melhores_res': 'Compare at least 3 different speeds for better results',
  'wx.comportamento': 'Behavior',
  'wx.comportamento_do_video_de_fundo': 'Background video behavior',
  'wx.configuracao_personalizada': 'Custom configuration',
  'wx.configuracoes': 'Settings',
  'wx.configuracoes_tecnicas': 'Technical settings',
  'wx.configure_como_o_autoplay_se_comporta': 'Configure how autoplay behaves',
  'wx.configure_os_botoes_de_reproducao_e_volume': 'Configure playback and volume buttons.',
  'wx.configure_quando_a_headline_aparece_e_desaparece': 'Configure when the headline appears and disappears',
  'wx.configure_tamanho_e_posicao_por_dispositivo': 'Configure size and position per device.',
  'wx.considere_o_publico_alvo_usuarios_mais_tecnicos_geralmente_s': 'Consider your audience: more technical users are usually more receptive to faster speeds.',
  'wx.conteudo': 'Content',
  'wx.conteudo_html': 'HTML content',
  'wx.conteudo_de_continuar_de_onde_parou': 'Continue Watching content',
  'wx.continuar_assistindo': 'Continue Watching',
  'wx.continuar_agora': 'Continue now',
  'wx.continuar_assistindo_2': 'Continue watching?',
  'wx.continuar_de_onde_parou': 'Continue where you left off',
  'wx.contraste_adequado_com_o_fundo': 'Proper contrast with background',
  'wx.controle_de_volume': 'Volume control',
  'wx.controles': 'Controls',
  'wx.controles_principais': 'Main controls',
  'wx.controles_de_reproducao': 'Playback controls',
  'wx.controles_do_player': 'Player controls',
  'wx.controles_foram_desabilitados_automaticamente_ao_ativar_auto': 'Controls were automatically disabled when enabling autoplay',
  'wx.conversao': 'Conversion:',
  'wx.conversoes': 'Conversions',
  'wx.conversoes_dividido_por_visualizacoes': 'Conversions divided by views',
  'wx.cor': 'Color',
  'wx.cor_primaria': 'Primary color',
  'wx.cor_secundaria': 'Secondary color',
  'wx.cor_solida': 'Solid color',
  'wx.cor_da_borda': 'Border color',
  'wx.cor_da_subheadline': 'Subheadline color',
  'wx.cor_de_fundo': 'Background color',
  'wx.cor_de_fundo_dos_controles': 'Controls background color',
  'wx.cor_de_fundo_e_opacidade_da_barra_inferior': 'Bottom bar background color and opacity.',
  'wx.cor_do_botao': 'Button color',
  'wx.cor_do_fundo': 'Background color',
  'wx.cor_do_texto': 'Text color',
  'wx.cor_do_icone': 'Icon color',
  'wx.cores_do_autoplay': 'Autoplay colors',
  'wx.cores_do_botao': 'Button colors',
  'wx.cores_do_overlay': 'Overlay colors',
  'wx.cores_do_player': 'Player colors',
  'wx.cores_do_overlay_2': 'Overlay colors',
  'wx.crie_sua_primeira_variacao': 'Create your first variation',
  'wx.curva_de_retencao': 'Retention curve',
  'wx.custom': 'Custom',
  'wx.custom_icon': 'Custom icon',
  'wx.custom_overlay': 'Custom overlay',
  'wx.codigo_html': 'HTML code',
  'wx.codigo_html_personalizado': 'Custom HTML code',
  'wx.codigo_copiado_para_a_area_de_transferencia': 'Code copied to clipboard',
  'wx.codigo_copiado_para_a_area_de_transferencia_2': 'Code copied to clipboard!',
  'wx.codigo_copiado': 'Code copied!',
  'wx.dados_resetados_com_sucesso': 'Data reset successfully!',
  'wx.data_invalida': 'Invalid date',
  'wx.de': 'From',
  'wx.defina_uma_velocidade_fixa_ou_rode_um_teste_automatico_para_': 'Set a fixed speed or run an auto test.',
  'wx.desabilitar_controles': 'Disable controls',
  'wx.destaca_o_botao_central_de_play': 'Highlights the central play button',
  'wx.dica_clique_na_caixa_no_player_para_mover_e_redimensionar': 'Tip: click the box in the player to move and resize.',
  'wx.digite_o_novo_texto': 'Type the new text',
  'wx.digite_o_texto_da_subheadline_opcional': 'Type the subheadline text (optional)',
  'wx.digite_o_texto_principal': 'Type the main text...',
  'wx.digite_o_texto_secundario': 'Type the secondary text...',
  'wx.digite_um_nome_para_a_variacao': 'Type a name for the variation',
  'wx.direita': 'Right',
  'wx.distribuicao_de_trafego_atualizada_com_sucesso': 'Traffic distribution updated successfully!',
  'wx.duracao_da_exibicao_segundos': 'Display duration (seconds)',
  'wx.duracao_do_teste': 'Test duration',
  'wx.editar': 'Edit',
  'wx.em_execucao': 'Running',
  'wx.encerra_o_teste_e_mantem_a_variacao_vencedora_ativa': 'Ends the test and keeps the winning variation active.',
  'wx.engajamento': 'Engagement',
  'wx.enviar_imagem': 'Upload image',
  'wx.enviar_icone_svg_recomendado': 'Upload icon (SVG recommended)',
  'wx.envie_uma_imagem_para_o_template_personalizado': 'Upload an image for the custom template',
  'wx.escolha_o_estilo_visual_do_botao_de_play_do_seu_player': 'Choose your player\'s play button visual style.',
  'wx.escolha_o_icone': 'Choose icon',
  'wx.escolha_um_modelo_visual_para_o_overlay': 'Choose a visual template for the overlay',
  'wx.escolha_um_video_da_biblioteca': 'Choose a video from the library',
  'wx.esconder_inicio': 'Hide start',
  'wx.esquerda': 'Left',
  'wx.estilo': 'Style',
  'wx.estilo_avancado': 'Advanced style',
  'wx.estilo_da_barra': 'Bar style',
  'wx.estilo_da_headline': 'Headline style',
  'wx.estilo_da_subheadline': 'Subheadline style',
  'wx.estilo_do_autoplay': 'Autoplay style',
  'wx.estilo_do_icone': 'Icon style',
  'wx.estilo_e_comportamento': 'Style and behavior',
  'wx.evite_conclusoes_precipitadas_com_poucos_dados_espere_atingi': 'Avoid hasty conclusions with little data — wait until you reach the recommended minimums.',
  'wx.ex_ative_o_som_para_melhor_experiencia': 'E.g.: Enable sound for a better experience',
  'wx.ex_seu_video_ja_comecou': 'E.g.: Your video has started',
  'wx.ex_assista_ate_o_final_para_descobrir_o_metodo_que_esta_gera': 'E.g.: Watch until the end to discover the method generating sales every day',
  'wx.ex_autoplay_moderno_conversao_limpa': 'E.g.: Modern Autoplay, Clean Conversion...',
  'wx.ex_botao_de_checkout': 'E.g.: Checkout Button',
  'wx.ex_headline_moderna_conversao_limpa': 'E.g.: Modern Headline, Clean Conversion...',
  'wx.ex_mostrar_botao_de_compra': 'E.g.: Show buy button',
  'wx.ex_seu_video_ja_comecou_2': 'E.g.: Your video has started',
  'wx.ex_toque_para_ativar_o_som': 'E.g.: Tap to enable sound',
  'wx.ex_ativo_visivel_destaque': 'E.g.: active, visible, highlight',
  'wx.ex_color_background_display': 'E.g.: color, background, display',
  'wx.excluir': 'Delete',
  'wx.excluir_variacao': 'Delete variation?',
  'wx.exibe_modal_para_retomar_reproducao_de_onde_o_usuario_parou': 'Resumes the video where the user stopped.',
  'wx.exibe_uma_imagem_estatica_no_lugar_do_player': 'Shows a static image instead of the player',
  'wx.exibe_uma_mensagem_sutil_informando_que_o_video_iniciou_auto': 'Notifies that the video started automatically.',
  'wx.exibir_video_original': 'Show original video',
  'wx.exibir_barra_de_controles': 'Show control bar',
  'wx.exibicao_por_dispositivo': 'Display by device',
  'wx.experiencia_imersiva': 'Immersive experience',
  'wx.facilite_o_controle_de_tempo_durante_a_reproducao': 'Make time control easier during playback.',
  'wx.familia_da_fonte': 'Font family',
  'wx.finalizar_teste_a_b': 'Finish A/B test?',
  'wx.forma_do_botao': 'Button shape',
  'wx.formato_da_imagem': 'Image format',
  'wx.fundo': 'Background',
  'wx.fundo_botao': 'Button background',
  'wx.fundo_da_headline': 'Headline background',
  'wx.futuras_integracoes': 'Future integrations',
  'wx.gratis_segredo_exclusivo_novo': 'Free, Secret, Exclusive, New',
  'wx.html_personalizado': 'Custom HTML',
  'wx.habilita_a_sincronizacao_inteligente_de_acoes_com_o_video': 'Enables smart action syncing with the video',
  'wx.habilita_o_botao_de_acao_no_video_selecionado': 'Enables the action button on the selected video',
  'wx.headline': 'Headline',
  'wx.headline_principal': 'Main headline',
  'wx.headline_nao_ativada': 'Headline not enabled',
  'wx.headline_por_dispositivo': 'Headline by device',
  'wx.herdando_valores_do_desktop_altere_para_personalizar': 'Inheriting values from Desktop. Change to customize.',
  'wx.hover_fundo': 'Hover background',
  'wx.hover_texto': 'Hover text',
  'wx.ideal_para_videos_corporativos_e_educacionais': 'Ideal for corporate and educational videos',
  'wx.imagem': 'Image',
  'wx.imagem_desktop': 'Desktop image',
  'wx.imagem_mobile': 'Mobile image',
  'wx.imagem_carregada_com_sucesso': 'Image uploaded successfully!',
  'wx.imagem_de_fundo': 'Background image',
  'wx.imagem_do_autoplay': 'Autoplay image',
  'wx.imagem_do_continuar_de_onde_parou': 'Continue Watching image',
  'wx.imagem_removida': 'Image removed',
  'wx.inferior_direito': 'Bottom Right',
  'wx.inferior_esquerdo': 'Bottom Left',
  'wx.inicia_o_video_automaticamente_ao_entrar_na_pagina': 'Starts the video automatically when entering the page.',
  'wx.iniciar_sem_som': 'Start muted',
  'wx.interacao_com_video': 'Video interaction',
  'wx.inicio': 'Start',
  'wx.inicio_mm_ss': 'Start (mm:ss)',
  'wx.link_de_destino': 'Destination link',
  'wx.link_do_botao': 'Button link',
  'wx.loop_continuo': 'Continuous loop',
  'wx.mantem_o_video_padrao_neste_dispositivo': 'Keeps the default video on this device',
  'wx.melhor_para_captar_atencao_e_criar_urgencia': 'Best for grabbing attention and creating urgency',
  'wx.melhor_plataforma': 'Best platform',
  'wx.mensagem': 'Message',
  'wx.mensagem_personalizada': 'Custom message',
  'wx.meu_titulo_personalizado': 'My custom title',
  'wx.modo_html': 'HTML mode',
  'wx.modo_html_avancado': 'HTML mode (Advanced)',
  'wx.modo_imagem': 'Image mode',
  'wx.modo_texto': 'Text mode',
  'wx.modo_de_exibicao': 'Display mode',
  'wx.mostra_o_progresso_do_video': 'Shows video progress',
  'wx.mostra_o_progresso_visual': 'Shows visual progress',
  'wx.mostra_os_controles_na_parte_inferior_do_player': 'Shows controls at the bottom of the player',
  'wx.mostra_um_video_diferente_apenas_neste_dispositivo': 'Shows a different video only on this device',
  'wx.mostrar_imagem': 'Show image',
  'wx.mostrar_barra_de_progresso': 'Show progress bar',
  'wx.mostrar_contagem_regressiva': 'Show countdown',
  'wx.mostrar_controles': 'Show controls',
  'wx.mostrar_controles_do_video_durante_o_autoplay': 'Show video controls during autoplay',
  'wx.mostrar_elemento': 'Show element',
  'wx.mostre_visualmente_o_avanco_do_video_e_aumente_a_retencao': 'Show video progress and boost retention.',
  'wx.muito_rapido_ou_muito_lento': 'Too fast or too slow',
  'wx.maximo_8_10_palavras': 'Max 8-10 words',
  'wx.maximo_de_5_modelos_simultaneos_permitidos': 'Max 5 simultaneous templates allowed',
  'wx.medio': 'Medium',
  'wx.metricas_rastreadas': 'Tracked metrics:',
  'wx.metricas_atualizadas_para_novo_formato': 'Metrics updated to new format!',
  'wx.minimo_2_variacoes': 'Minimum: 2 variations',
  'wx.navegacao_do_video': 'Video navigation',
  'wx.nenhum_teste_ativo': 'No active test',
  'wx.nenhum_video_encontrado': 'No videos found',
  'wx.nenhuma_variacao_configurada': 'No variation configured',
  'wx.nome_da_acao': 'Action name *',
  'wx.nome_da_classe': 'Class name',
  'wx.nome_da_variacao': 'Variation name',
  'wx.novo_texto': 'New text',
  'wx.o_tempo_de_pitch_e': 'The Pitch time is:',
  'wx.oculta_os_controles_ate_a_primeira_interacao': 'Hides controls until the first interaction',
  'wx.ocultar_pause': 'Hide pause',
  'wx.ocultar_video': 'Hide video',
  'wx.ocultar_elemento': 'Hide element',
  'wx.opacidade_do_fundo': 'Background opacity',
  'wx.open_sans': 'Open Sans',
  'wx.open_sans_classic': 'Open Sans (Classic)',
  'wx.ordem_atualizada': 'Order updated',
  'wx.overlay_personalizado': 'Custom overlay',
  'wx.para_conteudos_educativos_priorize_o_engajamento_para_vendas': 'For educational content, prioritize engagement; for sales, prioritize conversion rate',
  'wx.para_usar_a_barra_de_progresso_personalizada_desative_os_con': 'To use the custom Progress Bar, disable the player controls.',
  'wx.percentual_de_visitantes_que_iniciaram_o_video': 'Percent of visitors who started the video',
  'wx.percentual_medio_de_retencao_do_video_assistido': 'Average retention percentage of the video watched',
  'wx.perfeito_para_vendas_e_call_to_actions': 'Perfect for sales and call-to-actions',
  'wx.permita_interacao_com_o_video_e_aumente_controle_retencao_e_': 'Enable player controls for the viewer.',
  'wx.permite_ajustar_o_audio': 'Lets you adjust audio',
  'wx.permite_que_o_espectador_retome_o_video_de_onde_parou': 'Lets the viewer resume the video where they left off.',
  'wx.personalizado': 'Custom',
  'wx.personalizacao_visual': 'Visual customization',
  'wx.personalize_a_aparencia_do_autoplay': 'Customize the Autoplay appearance',
  'wx.personalize_cores_dos_controles_e_da_barra_inferior': 'Customize colors of controls and the bottom bar.',
  'wx.personalize_cores_animacoes_e_efeitos_visuais': 'Customize colors, animations and visual effects',
  'wx.personalize_o_comportamento_do_player_para_cada_tipo_de_disp': 'Adjust the player for each device type.',
  'wx.player_simulado': 'Simulated player',
  'wx.plays_unicos': 'Unique plays',
  'wx.plays_unicos_por_usuario': 'Unique plays per user',
  'wx.posicao': 'Position',
  'wx.posicao_h': 'H Position (%)',
  'wx.posicao_v': 'V Position (%)',
  'wx.posicao_do_botao_no_video': 'Button position in the video',
  'wx.posicao_e_tamanho': 'Position and size',
  'wx.preencha_headline_ou_subheadline': 'Fill in Headline or Subheadline.',
  'wx.preencha_todos_os_campos_obrigatorios': 'Fill in all required fields',
  'wx.preview': 'Preview',
  'wx.preview_configurado': 'Configured preview:',
  'wx.preview_de_frames_na_timeline': 'Frame preview on the timeline',
  'wx.preview_disponivel_no_player_real': 'Preview available in the real player',
  'wx.preview_do_botao': 'Button preview',
  'wx.preview_do_player': 'Player preview',
  'wx.progresso_do_teste': 'Test progress',
  'wx.pre_visualizacoes_de_estilos': 'Style previews',
  'wx.pre_visualizacao_ilustrativa': 'Illustrative preview',
  'wx.pre_visualizacao_ilustrativa_da_barra_de_progresso': 'Illustrative preview of the progress bar',
  'wx.pre_visualizacao_ilustrativa_do_html_personalizado': 'Illustrative preview of the custom HTML',
  'wx.pre_visualizacao_ilustrativa_do_botao_no_video': 'Illustrative preview of the button in the video',
  'wx.previa_do_botao': 'Button preview',
  'wx.pulsar': 'Pulse',
  'wx.quando_a_headline_aparece': 'When the headline appears',
  'wx.quando_a_headline_desaparece': 'When the headline disappears',
  'wx.quando_ativado_o_video_reinicia_do_inicio_ao_clicar': 'When enabled, the video restarts from the beginning on click',
  'wx.realize_testes_com_pelo_menos_100_visualizacoes_por_velocida': 'Run tests with at least 100 views per speed',
  'wx.recomenda_se_um_minimo_de_800_plays_unicos_por_variacao_para': 'A minimum of 800 unique plays per variation is recommended for statistically significant results',
  'wx.recomendado': 'Recommended',
  'wx.recomecar_video_apos_o_fim': 'Restart video after the end',
  'wx.reiniciar_video': 'Restart video',
  'wx.remove_completamente_o_player_neste_dispositivo': 'Completely removes the player on this device',
  'wx.remove_distracao_visual': 'Removes visual distraction',
  'wx.remover_imagem': 'Remove image',
  'wx.repetir_video_loop': 'Repeat video (loop)',
  'wx.reproducao_automatica': 'Autoplay',
  'wx.retencao_no_pitch': 'Retention at Pitch',
  'wx.retencao_no_pitch_2': 'Retention at pitch',
  'wx.retomada_automatica': 'Auto resume',
  'wx.retomar_reproducao': 'Resume playback',
  'wx.retangulo': 'Rectangle',
  'wx.selecione_a_posicao': 'Select position',
  'wx.selecione_a_velocidade_desejada': 'Select the desired speed',
  'wx.selecione_o_tamanho': 'Select size',
  'wx.selecione_pelo_menos_2_modelos_para_iniciar_o_teste': 'Select at least 2 templates to start the test',
  'wx.selecione_uma_animacao': 'Select an animation',
  'wx.sem_animacao': 'No animation',
  'wx.seu_html_aparecera_aqui': 'Your HTML will appear here',
  'wx.seu_html_aqui': 'Your HTML here',
  'wx.seu_video_ja_comecou': 'Your video has started',
  'wx.slide_de_baixo': 'Slide from bottom',
  'wx.sobre_a_reproducao_automatica': 'About autoplay:',
  'wx.som_de_alerta': 'Alert sound',
  'wx.status_do_teste': 'Test status',
  'wx.substituir_por_outro_video': 'Replace with another video',
  'wx.solido': 'Solid',
  'wx.tamanho': 'Size',
  'wx.tamanho_da_fonte': 'Font size',
  'wx.tamanho_do_icone': 'Icon size',
  'wx.tamanho_e_posicao': 'Size and position',
  'wx.tamanho_recomendado_1920x1080px_formatos_jpg_png_gif_maximo_': 'Recommended size: 1920x1080px • Formats: JPG, PNG, GIF • Max: 10MB',
  'wx.taxa_de_conversao': 'Conversion rate',
  'wx.tempo': 'Time',
  'wx.tempo_atual': 'Current time',
  'wx.tempo_atual_2': 'Current time',
  'wx.tempo_ate_retomar': 'Time until resume',
  'wx.tempo_de_exibicao': 'Display time',
  'wx.tempo_do_video_mm_ss': 'Video time (mm:ss) *',
  'wx.tempo_para_exibicao_segundos': 'Display delay (seconds)',
  'wx.temporizacao': 'Timing',
  'wx.teste_a_b_ativado': 'A/B test enabled!',
  'wx.teste_aplicado_com_sucesso': 'Test applied successfully!',
  'wx.teste_finalizado': 'Test finished',
  'wx.teste_finalizado_com_sucesso': 'Test finished successfully!',
  'wx.texto': 'Text',
  'wx.texto_botao': 'Button text',
  'wx.texto_inferior': 'Bottom text',
  'wx.texto_superior': 'Top text',
  'wx.texto_branco_em_fundo_claro': 'White text on light background',
  'wx.texto_da_headline': 'Headline text',
  'wx.texto_da_subheadline': 'Subheadline text',
  'wx.texto_do_botao': 'Button text',
  'wx.texto_do_botao_principal': 'Main button text',
  'wx.texto_do_link_secundario': 'Secondary link text',
  'wx.texto_exibido_no_overlay_para_orientar_o_usuario': 'Text shown in the overlay to guide the user',
  'wx.textos_do_overlay': 'Overlay texts',
  'wx.ticket_medio': 'Avg. ticket',
  'wx.tipo': 'Type',
  'wx.tipo_de_animacao': 'Animation type',
  'wx.tipo_de_acao': 'Action type *',
  'wx.tipo_de_fundo': 'Background type',
  'wx.tipo_de_som': 'Sound type',
  'wx.tipo_2': 'Type:',
  'wx.todos_os_videos': 'All videos',
  'wx.topo_direita': 'Top Right',
  'wx.topo_esquerda': 'Top Left',
  'wx.topo_pode_funcionar_melhor_que_rodape': 'Top may work better than bottom',
  'wx.total_de_visualizacoes_do_video': 'Total video views',
  'wx.trafego': 'Traffic',
  'wx.trafego_dividido_igualmente': 'Traffic split evenly',
  'wx.termino': 'End',
  'wx.termino_previsto': 'Expected end:',
  'wx.titulo': 'Title',
  'wx.titulo_opcional': 'Title (optional)',
  'wx.titulo_do_overlay': 'Overlay title',
  'wx.uma_mensagem_principal_por_vez': 'One main message at a time',
  'wx.upload_do_icone_personalizado': 'Custom icon upload',
  'wx.usar_cor_do_play': 'Use Play color',
  'wx.usar_velocidade_fixa': 'Use fixed speed',
  'wx.use_id_para_ids_ou_classe_para_classes_css_exemplo_meubotao_': 'Use #id for IDs or .class for CSS classes. Example: #myButton or .my-section',
  'wx.use_o_calendario_para_comparar_periodos_semelhantes': 'Use the calendar to compare similar periods',
  'wx.variacao': 'Variation',
  'wx.variacao_adicionada': 'Variation added!',
  'wx.variacao_atualizada': 'Variation updated!',
  'wx.variacao_excluida': 'Variation deleted',
  'wx.variacao_removida': 'Variation removed!',
  'wx.variacao_salva': 'Variation saved!',
  'wx.variacoes': 'Variations',
  'wx.velocidade': 'Speed',
  'wx.velocidade_fixa': 'Fixed speed',
  'wx.velocidade_atual': 'Current speed',
  'wx.velocidade_final': 'Final speed',
  'wx.velocidade_inicial': 'Initial speed',
  'wx.vis_unicas': 'Unique views',
  'wx.visualizacoes': 'Views',
  'wx.visualizacoes_unicas': 'Unique views',
  'wx.visualizacoes_unicas_por_usuario': 'Unique views (per user)',
  'wx.voce_parou_em_um_ponto_importante_do_video': 'You stopped at an important point of the video.',
  'wx.volte_para_revisar_momentos': 'Go back to review moments',
  'wx.video_de_fundo_inteligente': 'Smart background video',
  'wx.ate_2': 'to',
  'wx.https_exemplo_com': 'https://example.com',
  'wx.https_exemplo_com_br': 'https://example.com',
  'wx.segundos': 'seconds',
  'wx.icone': 'Icon',
  'wx.icone_personalizado': 'Custom icon',
  'wx.icone_do_play': 'Play icon',
  'wx.analytics_avancados': '• Advanced analytics',
  'wx.animacoes_css': '• CSS animations',
  'wx.ate_3_variacoes_simultaneas': '• Up to 3 simultaneous variations',
  'wx.ctr_click_through_rate': '• CTR (Click-through Rate)',
  'wx.compressao_automatica': '• Auto compression',
  'wx.configuracoes_de_qualidade': '• Quality settings',
  'wx.controle_total_com_codigo': '• Full control with code',
  'wx.cores_personalizaveis': '• Customizable colors',
  'wx.cores_que_nao_contrastam': '• Colors that don\'t contrast:',
  'wx.crie_urgencia': '• Create urgency:',
  'wx.codigo_de_embed': '• Embed code',
  'wx.divisao_automatica_de_trafego': '• Automatic traffic split',
  'wx.editor_visual_intuitivo': '• Intuitive visual editor',
  'wx.engajamento_2': '• Engagement',
  'wx.headlines_genericas': '• Generic headlines:',
  'wx.integracao_com_cdn': '• CDN integration',
  'wx.integracao_com_js': '• JS integration',
  'wx.mantenha_legivel': '• Keep it readable:',
  'wx.metricas_em_tempo_real': '• Real-time metrics',
  'wx.multiplos_focos': '• Multiple focuses:',
  'wx.otimizacao_de_performance': '• Performance optimization',
  'wx.para_desenvolvedores': '• For developers',
  'wx.posicao_que_cobre_conteudo': '• Position that covers content:',
  'wx.preview_em_tempo_real': '• Real-time preview',
  'wx.seja_especifico': '• Be specific:',
  'wx.tamanho_maximo_2mb': '• Max size: 2MB',
  'wx.taxa_de_conversao_2': '• Conversion rate',
  'wx.tempo_medio_de_exibicao': '• Avg. display time',
  'wx.teste_posicoes': '• Test positions:',
  'wx.texto_muito_longo': '• Text too long:',
  'wx.upload_de_imagens_gifs': '• Upload images/GIFs',
  'wx.use_numeros': '• Use numbers:',
  'wx.versoes_desktop_mobile': '• Desktop/mobile versions',
  'wx.alguns_navegadores_podem_restringir_o_audio_automatico_mesmo': '⚠️ Some browsers may restrict automatic audio even after visitor interaction.',
  'wx.velocidades_muito_altas_acima_de_1_5x_podem_prejudicar_a_com': '⚠️ Very high speeds (above 1.5x) can hurt content comprehension and trust.',
  'wx.requisito_autoplay_ativo': '⚡ Requirement: Autoplay enabled',
  'wx.faca': '✅ Do',
  'wx.modos_de_criacao': '🎨 Creation modes',
  'wx.melhores_praticas': '🎯 Best practices',
  'wx.dicas_de_templates': '💡 Template tips',

  'wx2.fontWeight': 'Font Weight',
  'wx2.all': 'All',
  'wx2.none': 'None',
  'wx2.fadeIn': 'Fade In',
  'wx2.fadeInHyphen': 'Fade-in',
  'wx2.loadBlock': 'Load block',
  'wx2.status': 'Status',
  'wx2.statusColon': 'Status:',
  'wx2.upload': 'Upload',
  'wx2.autoplayMessages': 'Autoplay messages',
  'wx2.showBorder': 'Show border',
  'wx2.saveProgressAuto': 'Automatically saves viewer progress',
  'wx2.continueWatching': 'Continue Watching',
  'wx2.pictureInPicture': 'Picture-in-Picture',
  'wx2.initialCounterValue': 'Initial counter value',
};

const es: Dict = {
  'panel.activateToConfigure': 'Activa el recurso para configurar',

  'ab.title': 'Botón de Acción',
  'ab.desc': 'Guía a tus espectadores con un CTA en el momento adecuado.',
  'ab.config': 'Configuración del Botón',
  'ab.config.desc': 'Define el texto y el enlace de tu CTA.',
  'ab.text': 'Texto del botón',
  'ab.link': 'Enlace del botón',
  'ab.appearance': 'Apariencia',
  'ab.appearance.desc': 'Personaliza los colores del botón.',
  'ab.bg': 'Fondo',
  'ab.fontColor': 'Texto',
  'ab.colorButton': 'Color del botón',
  'ab.colorText': 'Color del texto',
  'ab.showTime': 'Tiempo de exhibición',
  'ab.showTime.desc': 'Define cuándo aparecerá el botón en el video.',
  'ab.seconds': 'segundos',

  'sn.enable': 'Notificación de Ventas',
  'sn.enable.helper': 'Muestra ventas recientes para crear prueba social.',
  'sn.tab.basic': 'Configuraciones',
  'sn.tab.content': 'Contenido',
  'sn.tab.appearance': 'Apariencia',
  'sn.activeStatusTpl': 'Activo · 1 notificación cada {freq}s · {pos}',
  'sn.speed.title': 'Velocidad de las Notificaciones',
  'sn.speed.startDelay': 'Retraso inicial',
  'sn.speed.frequency': 'Frecuencia',
  'sn.speed.duration': 'Duración',
  'sn.position.title': 'Posición',
  'sn.pos.top-left': 'Superior Izquierda',
  'sn.pos.top-right': 'Superior Derecha',
  'sn.pos.bottom-left': 'Inferior Izquierda',
  'sn.pos.bottom-right': 'Inferior Derecha',
  'sn.loop.title': 'Repetir en bucle',
  'sn.loop.desc': 'Se reinicia automáticamente al mostrar todas.',
  'sn.interaction.title': 'Interacción',
  'sn.interaction.openLink': 'Al hacer clic, abrir enlace',
  'sn.interaction.openLinkDesc': 'Abre una URL al hacer clic en la notificación.',
  'sn.interaction.urlPlaceholder': 'https://ejemplo.com/oferta',
  'sn.interaction.urlHelp': 'Al hacer clic, abrir esta URL en una pestaña nueva.',
  'sn.interaction.urlInvalid': 'URL inválida. Usa http:// o https://. Ej: https://tu-sitio.com',
  'sn.interaction.urlAutofix': 'Sin http:// — se guardará como https://{url}',
  'sn.sound.title': 'Sonido',
  'sn.sound.desc': 'Reproduce un sonido junto a la notificación.',
  'sn.sound.volume': 'Volumen',
  'sn.sound.test': 'Probar sonido',
  'sn.message.title': 'Mensaje',
  'sn.message.product': 'Nombre del producto',
  'sn.message.productPlaceholder': 'Ej: Curso Premium',
  'sn.message.showPlatform': 'Mostrar plataforma',
  'sn.message.selectPlatform': 'Selecciona una plataforma',
  'sn.message.platformCustom': 'Personalizado',
  'sn.message.customLogo': 'Logo personalizado',
  'sn.message.changeLogo': 'Haz clic para cambiar',
  'sn.message.addLogo': 'Agregar logo',
  'sn.simulated.title': 'Datos simulados',
  'sn.simulated.names': 'Nombres',
  'sn.simulated.cities': 'Ciudades',
  'sn.simulated.auto': 'Sugerencia automática',
  'sn.simulated.recommended': '(recomendado)',
  'sn.simulated.custom': 'Lista personalizada',
  'sn.simulated.noLocation': 'No mostrar ubicación',
  'sn.simulated.typeEnter': 'Escribe y presiona Enter',
  'sn.simulated.add10': 'Agregar 10 ejemplos',
  'sn.template.title': 'Texto de la plantilla',
  'sn.template.placeholder': '{nombre} de {ciudad} acaba de comprar {producto}',
  'sn.template.insert': 'Insertar:',
  'sn.template.preview': 'Vista previa',
  'sn.appearance.title': 'Estilo',
  'sn.appearance.bg': 'Color de fondo',
  'sn.appearance.border': 'Color del borde',
  'sn.appearance.color': 'Color del texto',
  'sn.appearance.radius': 'Redondeo',
  'sn.appearance.showIcon': 'Mostrar ícono',
  'sn.quick.title': 'Ajustes rápidos',
  'sn.quick.size': 'Tamaño',
  'sn.size.small': 'Pequeño',
  'sn.size.medium': 'Mediano',
  'sn.size.large': 'Grande',
  'sn.disclaimer': 'Estas configuraciones no afectan el funcionamiento del video.',
  'sn.colorCustom': 'Color personalizado',
  'sn.addMore': 'Añadir...',
  'sn.fallbackPos': 'Inferior Derecha',

  'border.title': 'Borde del Reproductor',
  'border.desc': 'Destaca el reproductor con borde y sombra.',
  'border.appearance': 'Apariencia',
  'border.appearance.desc': 'Color, grosor y redondeo del borde.',
  'border.color': 'Color del borde',
  'border.width': 'Grosor',
  'border.radius': 'Redondeo',
  'border.clickToChange': 'Haz clic para cambiar',
  'border.shadow.toggle': 'Activar sombra',
  'border.shadow.toggleDesc': 'Añade profundidad y énfasis al reproductor.',
  'border.shadow.title': 'Configuración de Sombra',
  'border.shadow.desc': 'Ajusta color, desenfoque e intensidad.',
  'border.shadow.color': 'Color de la sombra',
  'border.shadow.blur': 'Desenfoque',
  'border.shadow.intensity': 'Intensidad',

  'controls.title': 'Controles del Video',
  'controls.desc': 'Configura el comportamiento de reproducción del video.',
  'controls.loopBehavior': 'Comportamiento de Bucle',
  'controls.loopBehavior.desc': 'Define si el video se repite automáticamente.',
  'controls.noRepeat': 'No repetir',
  'controls.repeat': 'Repetir',

  'ap.title': 'Pausa Inteligente',
  'ap.desc': 'Pausa el video cuando el espectador sale de la pestaña.',
  'ap.helper': 'Funciona cuando el visitante cambia de pestaña o minimiza el navegador.',
  'ap.on': 'Pausa inteligente activada',
  'ap.off': 'Pausa inteligente desactivada',
  'ap.active': 'Activo',
  'ap.onDesc': 'El video se pausa al salir de la pestaña y se reanuda automáticamente al volver.',
  'ap.offDesc': 'Activa el recurso arriba para pausar el video cuando el visitante salga de la pestaña.',

  'rae.title': 'Reiniciar el video al finalizar',
  'rae.desc': 'Reinicia el video cuando termina la reproducción.',
  'rae.helper': 'Funciona al finalizar la reproducción del video.',
  'rae.on': 'Reinicio automático activado',
  'rae.off': 'Reinicio automático desactivado',
  'rae.onDesc': 'Al finalizar, el video vuelve al inicio automáticamente, manteniendo la reproducción en bucle.',
  'rae.offDesc': 'Activa el recurso arriba para reiniciar el video automáticamente al llegar al final.',

  'aff.title': 'Activar enlace de afiliado',
  'aff.desc': 'Muestra el logo HostVSL y activa tu enlace de afiliado.',
  'aff.linkLabel': 'Tu enlace de afiliado',
  'aff.linkPlaceholder': 'https://hostvsl.com/?ref=TU_ID',
  'aff.linkInvalid': 'Este enlace no parece válido. Verifícalo e inténtalo de nuevo.',
  'aff.linkHelper': 'Ingresa tu enlace de afiliado para ganar comisiones. El logo de HostVSL aparecerá en la esquina superior derecha del reproductor y al hacer clic abrirá tu enlace en una nueva pestaña.',
  'aff.previewTitle': 'El logo de HostVSL se mostrará en el reproductor.',
  'aff.previewClick': 'El clic del usuario abrirá:',

  'pix.tokenCopied': '¡Token copiado!',
  'pix.facebook': 'Facebook',

  'cw.title': 'Continuar donde lo dejaste',
  'cw.desc': 'Permite que los usuarios reanuden el video donde lo dejaron',
  'cw.tab.templates': 'Plantillas',
  'cw.tab.customize': 'Personalización',
  'cw.templates.helper': 'Selecciona una plantilla para la pantalla de retorno',
  'cw.templates.resume': 'Reanudar',
  'cw.upload.title': 'Subida Personalizada',
  'cw.upload.subtitle': 'Arrastra tu archivo o haz clic para subir',
  'cw.upload.maxSize': 'Tamaño máximo: 2MB',
  'cw.upload.swap': 'Cambiar',
  'cw.upload.remove': 'Eliminar',
  'cw.customize.soon': 'Opciones de personalización próximamente.',

  'th.title': 'Miniaturas del Video',
  'th.desc': 'Define imágenes estratégicas para reforzar la atención.',
  'th.subhint': 'Inicio, pausa y final pueden tener imágenes diferentes.',
  'th.tab.initial': 'Inicio',
  'th.tab.pause': 'Pausa',
  'th.tab.end': 'Final',
  'th.toast.added.title': 'Miniatura añadida con éxito',
  'th.toast.added.initial': 'La miniatura de inicio fue configurada',
  'th.toast.added.pause': 'Configura el botón de acción si lo deseas',
  'th.toast.added.end': 'La miniatura de final fue configurada',
  'th.toast.removed.title': 'Miniatura eliminada con éxito',
  'th.toast.removed.desc': 'La miniatura fue eliminada',
  'th.toast.mode.title': 'Modo actualizado',
  'th.toast.mode.always': 'La miniatura se mostrará siempre que se pause',
  'th.toast.mode.range': 'La miniatura se mostrará solo en el tramo específico',
  'th.toast.button.added.title': 'Botón añadido',
  'th.toast.button.added.desc': 'Configura el botón y visualízalo en la previsualización',
  'th.toast.button.removed.title': 'Botón eliminado',
  'th.toast.button.removed.desc': 'El botón fue eliminado de la miniatura de pausa',
  'th.button.defaultText': 'Continuar Viendo',

  'fc.title': 'Cuenta Regresiva Final Inteligente',
  'fc.desc': 'Muestra una cuenta regresiva al final del video y redirige automáticamente.',
  'fc.tab.settings': 'Configuraciones',
  'fc.tab.style': 'Estilo',
  'fc.essential': 'Esencial',
  'fc.timeLabel': 'Tiempo de la cuenta',
  'fc.redirect': 'Redirección',
  'fc.onEnd': 'Al finalizar',
  'fc.action.redirect': 'Redirigir automáticamente al terminar',
  'fc.action.warning': 'Solo mostrar el aviso',
  'fc.urlLabel': 'URL de destino',
  'fc.urlPlaceholder': 'https://tu-checkout.com',
  'fc.url.empty': 'Ingresa una URL para activar la redirección.',
  'fc.url.invalid': 'URL inválida. Ej: https://tu-sitio.com',
  'fc.url.autofix': 'Sin http:// — se guardará como https://{url}',
  'fc.button.noTarget': 'Sin una URL de redirección válida el botón no se muestra al espectador.',
  'fc.dismiss': 'Cerrar y volver al video',
  'fc.linkOpen': 'Apertura del enlace',
  'fc.openSame': 'Abrir en la misma pestaña',
  'fc.openNew': 'Abrir en una pestaña nueva',
  'fc.warningOnly': 'Estas opciones se usan solo cuando la redirección automática está activa.',
  'fc.cardContent': 'Contenido del Card',
  'fc.cardTitle': 'Título del Card',
  'fc.cardTitlePlaceholder': '¡Oferta Especial Liberada!',
  'fc.cardDesc': 'Descripción',
  'fc.cardDescPlaceholder': 'Serás redirigido en unos segundos…',
  'fc.cardDescHelp': 'La cuenta se mostrará automáticamente según el tiempo definido.',
  'fc.button': 'Botón "Ir Ahora"',
  'fc.button.show': 'Mostrar botón',
  'fc.button.text': 'Texto del botón',
  'fc.button.textPlaceholder': 'Ir Ahora',
  'fc.button.action': 'Acción del botón',
  'fc.appearance': 'Apariencia',
  'fc.displayMode': 'Modo de visualización',
  'fc.mode.overlay': 'Superponer en el video',
  'fc.mode.fullscreen': 'Pantalla completa',
  'fc.preset': 'Preset de estilo',
  'fc.preset.host': 'Predeterminado Host',
  'fc.preset.host.sub': 'Recomendado',
  'fc.preset.minimal': 'Discreto',
  'fc.preset.minimal.sub': 'Suave y limpio',
  'fc.quick': 'Ajustes rápidos',
  'fc.quick.opacity': 'Opacidad del fondo',
  'fc.quick.radius': 'Radio del card',
  'fc.quick.mainColor': 'Color principal',
  'fc.quick.mainColorName': 'Color Principal',
  'fc.quick.mainColorHelp': 'Aplicado al botón y detalles',
  'fc.quick.bgColor': 'Color de fondo',
  'fc.quick.customColor': 'Color Personalizado',
  'fc.count': 'Cuenta',
  'fc.count.circle': 'Color del Círculo',
  'fc.count.number': 'Color del Número',
  'fc.count.size': 'Tamaño',
  'fc.size.s': 'P',
  'fc.size.m': 'M',
  'fc.size.l': 'G',
  'fc.typography': 'Tipografía',
  'fc.typo.title': 'Color del Título',
  'fc.typo.desc': 'Color de la Descripción',
  'fc.btnGroup': 'Botón',
  'fc.btn.color': 'Color del Botón',
  'fc.btn.text': 'Color del Texto',
  'fc.btn.radius': 'Radio',

  // Logo
  'lg.title': 'Logo',
  'lg.desc': 'Muestra tu logotipo sobre el video.',
  'lg.tab.config': 'Configuración',
  'lg.tab.appearance': 'Apariencia',
  'lg.upload': 'Subir Logo',
  'lg.uploadError.format': 'Formato no soportado. Usa PNG o JPG.',
  'lg.uploadError.size': 'Archivo mayor que {n}MB',
  'lg.uploadError.generic': 'Error al procesar el archivo',
  'lg.link': 'Enlace de destino',
  'lg.link.placeholder': 'tusitio.com',
  'lg.link.invalid': 'URL inválida',
  'lg.link.invalidDomain': 'Ingresa un dominio válido. Ej: tusitio.com',
  'lg.link.savedAs': '→ Se guardará como:',
  'lg.link.helper': 'Acepta: tusitio.com, www.tusitio.com o https://tusitio.com',
  'lg.utm.enable': 'Añadir parámetros UTM',
  'lg.utm.source': 'utm_source (ej: youtube)',
  'lg.utm.medium': 'utm_medium (ej: video)',
  'lg.utm.campaign': 'utm_campaign (ej: lanzamiento)',
  'lg.posSize': 'Posición y Tamaño',
  'lg.posLabel': 'Posición en el reproductor',
  'lg.fineTune': 'Ajustes finos',
  'lg.offsetX': 'Desplazamiento Horizontal',
  'lg.offsetY': 'Desplazamiento Vertical',
  'lg.sizeOpacity': 'Tamaño y Transparencia',
  'lg.size': 'Tamaño',
  'lg.opacity': 'Transparencia',
  'lg.border.add': 'Añadir borde',
  'lg.border.color': 'Color del borde',
  'lg.border.width': 'Grosor',
  'lg.shadow.add': 'Añadir sombra',
  'lg.shadow.color': 'Color de la sombra',
  'lg.shadow.blur': 'Desenfoque',
  'lg.shadow.intensity': 'Intensidad',
  'lg.pos.top-left': 'Superior Izquierda',
  'lg.pos.top-center': 'Superior Centro',
  'lg.pos.top-right': 'Superior Derecha',
  'lg.pos.center-left': 'Centro Izquierda',
  'lg.pos.center': 'Centro',
  'lg.pos.center-right': 'Centro Derecha',
  'lg.pos.bottom-left': 'Inferior Izquierda',
  'lg.pos.bottom-center': 'Inferior Centro',
  'lg.pos.bottom-right': 'Inferior Derecha',
  // Mini-Hook
  'mh.title': 'Mini-Hook',
  'mh.desc': 'Muestra imágenes o GIFs en momentos estratégicos.',
  'mh.image': 'Imagen',
  'mh.delete': 'Eliminar',
  'mh.empty.title': 'Comienza con tu primer Mini-Hook',
  'mh.empty.desc': 'Usa Mini-Hooks para mostrar imágenes o GIFs en momentos estratégicos. Ideal para reforzar promesas, crear avisos visuales y recuperar atención.',
  'mh.empty.cta': 'Crear primer Mini-Hook',
  'mh.count.suffix': 'Mini-Hook',
  'mh.count.suffixPlural': 'Mini-Hooks',
  'mh.activeSuffix': 'activo',
  'mh.activeSuffixPlural': 'activos',
  'mh.new': 'Nuevo Mini-Hook',
  'mh.search': 'Buscar Mini-Hook',
  'mh.back': 'Volver',
  'mh.name': 'Nombre interno',
  'mh.name.placeholder': 'Ej: Refuerzo de la promesa inicial',
  'mh.imageOrGif': 'Imagen o GIF',
  'mh.upload.cta': 'Haz clic o arrastra el archivo aquí',
  'mh.upload.formats': 'JPG, PNG, WEBP y GIF • hasta 20 MB',
  'mh.file.default': 'Archivo',
  'mh.file.gif': 'GIF animado',
  'mh.file.image': 'Imagen estática',
  'mh.file.remove': 'Quitar',
  'mh.file.swap': 'Cambiar',
  'mh.opt.analyzing': 'Analizando…',
  'mh.opt.optimizing': 'Optimizando…',
  'mh.opt.light': 'Archivo ligero',
  'mh.opt.heavy': 'Puede impactar el rendimiento',
  'mh.opt.heavyGif': 'Este GIF es pesado ({size}) y puede afectar la fluidez del preview. Recomendamos usar un GIF más ligero o reducir las dimensiones.',
  'mh.opt.auto': 'Optimizar automáticamente',
  'mh.opt.autoDesc': 'Los GIFs optimizados hacen el Mini-Hook más fluido.',
  'mh.timing': 'Temporización',
  'mh.timing.start': 'Aparecer en',
  'mh.timing.end': 'Desaparecer en',
  'mh.opacity': 'Opacidad',
  'mh.cancel': 'Cancelar',
  'mh.save': 'Guardar cambios',
  'mh.create': 'Crear Mini-Hook',
  'mh.toast.tooLarge': 'Archivo demasiado grande. Máximo: 20 MB',
  'mh.toast.invalidFormat': 'Formato inválido. Usa JPG, PNG, WEBP o GIF.',
  'mh.toast.optimized': 'GIF optimizado • {n}% más ligero',
  'mh.toast.heavyWarn': 'GIF pesado puede afectar la fluidez del preview.',
  'mh.toast.updated': 'Mini-Hook actualizado con éxito',
  'mh.toast.created': 'Mini-Hook creado con éxito',
  'mh.toast.duplicated': 'Mini-Hook duplicado con éxito',
  'mh.toast.removed': 'Mini-Hook eliminado con éxito',
  'mh.toast.overlap': 'Este Mini-Hook se superpone con "{name}" en el mismo intervalo.',
  'mh.copy.suffix': '(copia)',
  'mh.validate.upload': 'Sube una imagen o GIF para continuar.',
  'mh.validate.name': 'Ingresa un nombre para identificar este Mini-Hook.',
  'mh.validate.endGtStart': 'El tiempo de salida debe ser mayor que el de entrada.',
  'mh.confirm.title': '¿Eliminar Mini-Hook?',
  'mh.confirm.desc': '¿Seguro que deseas eliminar este Mini-Hook? Esta acción no se puede deshacer.',
  'mh.confirm.cancel': 'Cancelar',
  'mh.confirm.delete': 'Eliminar',
  // Subtitles
  'sb.title': 'Subtítulos',
  'sb.desc': 'Añade subtítulos para mejorar la accesibilidad.',
  'sb.empty.title': 'Configura tus subtítulos',
  'sb.empty.desc': 'Con los subtítulos activados, puedes añadir archivos .SRT o .VTT a tu video. El espectador podrá activarlos o desactivarlos con el botón CC.',
  'sb.empty.hint': 'Activa el switch arriba para empezar a configurar tus subtítulos.',
  'sb.help': 'Aprende sobre subtítulos',
  'sb.help.tip': 'Sube un archivo .VTT o .SRT con los subtítulos sincronizados. Ambos formatos son compatibles.',
  'sb.file.title': 'Archivo de subtítulos',
  'sb.file.desc': 'Sube el archivo .VTT o .SRT con los subtítulos sincronizados.',
  'sb.upload.cta': 'Haz clic o arrastra el archivo .VTT o .SRT',
  'sb.upload.formats': 'Formatos aceptados: .VTT, .SRT',
  'sb.upload.error': 'Archivo inválido o corrupto. No se encontraron subtítulos.',
  'sb.upload.template': 'Descarga el archivo modelo para ver el formato correcto',
  'sb.help.section': '¿Necesitas ayuda para crear tus subtítulos?',
  'sb.help.download': 'Descargar modelo (.TXT)',
  'sb.help.downloadDesc': 'Descarga un ejemplo simple para rellenar tus subtítulos.',
  'sb.help.convert': 'Convertir a VTT',
  'sb.help.convertDesc': 'Convierte tu texto al formato aceptado.',
  'sb.file.loaded': 'Cargado',
  'sb.file.cuesOne': 'subtítulo',
  'sb.file.cuesMany': 'subtítulos',
  'sb.file.edit': 'Editar subtítulo',
  'sb.file.replace': 'Reemplazar archivo',
  'sb.file.remove': 'Quitar subtítulo',
  'sb.preview.title': 'Vista previa',
  'sb.preview.search': 'Buscar subtítulo...',
  'sb.preview.empty': 'No se encontraron subtítulos para "{q}"',
  'sb.preview.showLess': 'Mostrar menos',
  'sb.preview.showAll': 'Ver todos ({n})',
  'sb.cue.editText': 'Editar texto',
  'sb.cue.delete': 'Eliminar subtítulo',
  'sb.cue.editTime': 'Haz clic para editar tiempo',
  'sb.editor.title': 'Editar contenido VTT',
  'sb.editor.save': 'Guardar',
  'sb.editor.cancel': 'Cancelar',
  'sb.toast.timeInvalid': 'Tiempo inválido. Usa el formato m:ss.s y asegura que inicio < fin.',
  'sb.toast.timeUpdated': '¡Tiempo actualizado!',
  'sb.toast.textEmpty': 'El texto del subtítulo no puede estar vacío.',
  'sb.toast.textUpdated': '¡Texto actualizado!',
  'sb.toast.confirmDelete': '¿Seguro que deseas eliminar este subtítulo?',
  'sb.toast.lastRemoved': 'Último subtítulo eliminado. Archivo borrado.',
  'sb.toast.deleted': '¡Subtítulo eliminado!',
  'sb.toast.reordered': '¡Orden actualizado!',
  'sb.settings.title': 'Configuración de subtítulos',
  'sb.settings.desc': 'Personaliza cómo se muestran los subtítulos en el reproductor.',
  'sb.settings.showByDefault': 'Mostrar subtítulos por defecto',
  'sb.settings.showByDefaultDesc': 'Los subtítulos comienzan activos al cargar el video',
  'sb.settings.ccButton': 'Mostrar botón de subtítulos',
  'sb.settings.ccButtonDesc': 'Permite que el espectador los active o desactive',
  'sb.settings.warn': 'Los subtítulos quedarán desactivados para el espectador.',
  'sb.settings.fontSize': 'Tamaño de fuente',
  'sb.settings.align': 'Alineación',
  'sb.settings.textColor': 'Color del texto',
  'sb.settings.bgColor': 'Color del fondo',
  'sb.settings.bgOpacity': 'Opacidad del fondo',
  'sb.style.title': 'Estilo del subtítulo',
  'sb.style.desc': 'Elige cómo aparece el subtítulo en el reproductor.',
  'sb.style.animType': 'Tipo de animación',
  'sb.anim.none': 'Estándar',
  'sb.anim.noneDesc': 'Sin animación',
  'sb.anim.fade': 'Fade suave',
  'sb.anim.fadeDesc': 'Transición suave',
  'sb.anim.pop': 'Pop / Destaque',
  'sb.anim.popDesc': 'Escala con impacto',
  'sb.anim.word': 'Palabra por palabra',
  'sb.anim.wordDesc': 'Estilo TikTok',
  'sb.style.highlight': 'Estilo del destaque',
  'sb.hl.background': 'Fondo coloreado',
  'sb.hl.textOnly': 'Color del texto',
  'sb.hl.scale': 'Escala ligera',
  'sb.hl.color': 'Color del destaque',
  // === Auto-generated W4 sweep ===
  'wx.headlines_subheadlines_what': '📋 ¿Qué son los Titulares/Subtítulos?',
  'wx.confirm_delete_action': '¿Realmente quieres eliminar esta acción?',
  'wx.assista_esse_video': '"Mira este video"',
  'wx.ganhe_r_1_000_mes_vs_ganhe_dinheiro': '"Gana R$ 1.000/mes" vs "Gana dinero"',
  'wx.ultimas_24h_vagas_limitadas': '"Últimas 24h", "Cupos Limitados"',
  'wx.div_seu_html_aqui_div': '<div>Tu HTML aquí</div>',
  'wx.h1_meu_titulo_personalizado_h1_10_cole_seu_codigo_html_aqui': '<h1>Mi título personalizado</h1>&#10;Pega tu código HTML aquí...',
  'wx.a_distribuicao_precisa_somar_100': 'La distribución debe sumar 100%',
  'wx.a_reproducao_automatica_cria_a_sensacao_de_inicio_imediato_a': 'La reproducción automática crea la sensación de inicio inmediato, aumentando el engagement. El video comienza sin sonido para cumplir las políticas de los navegadores, y el overlay incentiva la activación del audio.',
  'wx.a_soma_da_distribuicao_de_trafego_deve_ser_100': 'La distribución de tráfico debe sumar 100%',
  'wx.adicionar_classe': 'Añadir clase',
  'wx.aguarde_pelo_menos_7_dias_para_que_o_teste_colete_dados_sufi': 'Espera al menos 7 días para que la prueba recopile datos suficientes',
  'wx.ajustamos_o_intervalo_para_manter_a_ordem_correta': 'Ajustamos el intervalo para mantener el orden correcto.',
  'wx.ajuste_a_aparencia_do_overlay_de_autoplay': 'Ajusta la apariencia del overlay de autoplay',
  'wx.ajuste_velocidade_e_visibilidade_dos_controles': 'Ajusta velocidad y visibilidad de los controles.',
  'wx.ajustes_finos': 'Ajustes finos',
  'wx.alterar_estilo': 'Cambiar estilo',
  'wx.alterar_texto': 'Cambiar texto',
  'wx.animacao_pulsar': 'Animación de pulso',
  'wx.animacao_de_entrada': 'Animación de entrada',
  'wx.ao_ativar_a_headline_sera_exibida_diretamente_sobre_o_video_': 'Muestra el titular directamente sobre el video.',
  'wx.ao_finalizar_a_velocidade_com_melhor_engajamento_sera_defini': 'Al finalizar, la velocidad con mejor engagement se definirá como principal.',
  'wx.aparencia': 'Apariencia',
  'wx.aparencia_do_player': 'Apariencia del Player',
  'wx.aparencia_dos_controles': 'Apariencia de los Controles',
  'wx.aplicada_em_barra_de_controle_play_volume_e_botoes_principai': 'Aplicada en barra de control, play, volumen y botones principales',
  'wx.aplicada_em_icones_secundarios_e_textos_do_player': 'Aplicada en iconos secundarios y textos del player',
  'wx.arraste_ou_clique_para_enviar': 'Arrastra o haz clic para subir',
  'wx.arraste_seu_arquivo_ou_clique_para_enviar': 'Arrastra tu archivo o haz clic para subir',
  'wx.arraste_sua_imagem_de_fundo': 'Arrastra tu imagen de fondo',
  'wx.assistir_do_inicio': 'Ver desde el inicio',
  'wx.ativar_autoplay': 'Activar Autoplay',
  'wx.ativar_acoes_sincronizadas': 'Activar Acciones Sincronizadas',
  'wx.ativar_continuar_assistindo': 'Activar Continuar Viendo',
  'wx.ativar_headline': 'Activar Titular',
  'wx.ativar_velocidade': 'Activar Velocidad',
  'wx.ativar_botao': 'Activar botón',
  'wx.ativar_tema_do_player': 'Activar tema del player',
  'wx.ativar_teste_automatico': 'Activar prueba automática',
  'wx.ative_o_autoplay_na_aba_configuracao_para_configurar_testes_': 'Activa el autoplay en la pestaña Configuración para configurar pruebas A/B',
  'wx.ative_o_autoplay_para_ver_o_preview': 'Activa el autoplay para ver la vista previa',
  'wx.ative_o_som': 'Activa el sonido',
  'wx.ate': 'Hasta',
  'wx.aumente_a_retencao_com_um_video_gancho_no_autoplay': 'Aumenta la retención con un video gancho en el autoplay',
  'wx.avalie_o_contexto_do_seu_conteudo_videos_tecnicos_podem_func': 'Evalúa el contexto de tu contenido — los videos técnicos pueden funcionar mejor a velocidades más bajas',
  'wx.avancado': 'Avanzado',
  'wx.avancar_10s': 'Avanzar 10s',
  'wx.acao': 'Acción',
  'wx.acao_atualizada_com_sucesso': '¡Acción actualizada con éxito!',
  'wx.acao_criada_com_sucesso': '¡Acción creada con éxito!',
  'wx.acao_excluida_com_sucesso': '¡Acción eliminada con éxito!',
  'wx.acoes': 'Acciones',
  'wx.baixo_direita': 'Inferior Derecha',
  'wx.baixo_esquerda': 'Inferior Izquierda',
  'wx.barra_inferior': 'Barra Inferior',
  'wx.barra_de_controles': 'Barra de Controles',
  'wx.barra_de_progresso': 'Barra de Progreso',
  'wx.botao_continuar': 'Botón continuar',
  'wx.botao_de_acao': 'Botón de Acción',
  'wx.botao_discreto_sobre_o_video': 'Botón discreto sobre el video',
  'wx.botao_play_pequeno': 'Botón play pequeño',
  'wx.botao_reiniciar': 'Botón reiniciar',
  'wx.buscar_videos': 'Buscar videos...',
  'wx.c_no_botao': 'Color del Botón',
  'wx.c_no_botao_2': 'Color del botón',
  'wx.click': 'Click',
  'wx.click_here': 'Click Aquí',
  'wx.cole_seu_codigo_html_aqui_10_aceita_html_css_inline_e_script': 'Pega tu código HTML aquí...&#10;Acepta HTML, CSS inline y scripts ligeros.',
  'wx.como_ficara_no_video': 'Cómo se verá en el video',
  'wx.compare_no_minimo_3_velocidades_diferentes_para_melhores_res': 'Compara al menos 3 velocidades diferentes para mejores resultados',
  'wx.comportamento': 'Comportamiento',
  'wx.comportamento_do_video_de_fundo': 'Comportamiento del Video de Fondo',
  'wx.configuracao_personalizada': 'Configuración Personalizada',
  'wx.configuracoes': 'Configuración',
  'wx.configuracoes_tecnicas': 'Configuración Técnica',
  'wx.configure_como_o_autoplay_se_comporta': 'Configura cómo se comporta el autoplay',
  'wx.configure_os_botoes_de_reproducao_e_volume': 'Configura los botones de reproducción y volumen.',
  'wx.configure_quando_a_headline_aparece_e_desaparece': 'Configura cuándo aparece y desaparece el titular',
  'wx.configure_tamanho_e_posicao_por_dispositivo': 'Configura tamaño y posición por dispositivo.',
  'wx.considere_o_publico_alvo_usuarios_mais_tecnicos_geralmente_s': 'Considera al público: los usuarios más técnicos suelen ser más receptivos a velocidades más rápidas.',
  'wx.conteudo': 'Contenido',
  'wx.conteudo_html': 'Contenido HTML',
  'wx.conteudo_de_continuar_de_onde_parou': 'Contenido de Continuar Viendo',
  'wx.continuar_assistindo': 'Continuar Viendo',
  'wx.continuar_agora': 'Continuar ahora',
  'wx.continuar_assistindo_2': '¿Continuar viendo?',
  'wx.continuar_de_onde_parou': 'Continuar donde lo dejaste',
  'wx.contraste_adequado_com_o_fundo': 'Contraste adecuado con el fondo',
  'wx.controle_de_volume': 'Control de volumen',
  'wx.controles': 'Controles',
  'wx.controles_principais': 'Controles Principales',
  'wx.controles_de_reproducao': 'Controles de Reproducción',
  'wx.controles_do_player': 'Controles del Player',
  'wx.controles_foram_desabilitados_automaticamente_ao_ativar_auto': 'Los controles fueron desactivados automáticamente al activar el autoplay',
  'wx.conversao': 'Conversión:',
  'wx.conversoes': 'Conversiones',
  'wx.conversoes_dividido_por_visualizacoes': 'Conversiones divididas por visualizaciones',
  'wx.cor': 'Color',
  'wx.cor_primaria': 'Color primario',
  'wx.cor_secundaria': 'Color secundario',
  'wx.cor_solida': 'Color sólido',
  'wx.cor_da_borda': 'Color del borde',
  'wx.cor_da_subheadline': 'Color del subtítulo',
  'wx.cor_de_fundo': 'Color de fondo',
  'wx.cor_de_fundo_dos_controles': 'Color de fondo de los controles',
  'wx.cor_de_fundo_e_opacidade_da_barra_inferior': 'Color de fondo y opacidad de la barra inferior.',
  'wx.cor_do_botao': 'Color del botón',
  'wx.cor_do_fundo': 'Color de fondo',
  'wx.cor_do_texto': 'Color del texto',
  'wx.cor_do_icone': 'Color del icono',
  'wx.cores_do_autoplay': 'Colores del Autoplay',
  'wx.cores_do_botao': 'Colores del botón',
  'wx.cores_do_overlay': 'Colores del overlay',
  'wx.cores_do_player': 'Colores del Player',
  'wx.cores_do_overlay_2': 'Colores del overlay',
  'wx.crie_sua_primeira_variacao': 'Crea tu primera variación',
  'wx.curva_de_retencao': 'Curva de Retención',
  'wx.custom': 'Personalizado',
  'wx.custom_icon': 'Icono personalizado',
  'wx.custom_overlay': 'Overlay personalizado',
  'wx.codigo_html': 'Código HTML',
  'wx.codigo_html_personalizado': 'Código HTML personalizado',
  'wx.codigo_copiado_para_a_area_de_transferencia': 'Código copiado al portapapeles',
  'wx.codigo_copiado_para_a_area_de_transferencia_2': '¡Código copiado al portapapeles!',
  'wx.codigo_copiado': '¡Código copiado!',
  'wx.dados_resetados_com_sucesso': '¡Datos restablecidos con éxito!',
  'wx.data_invalida': 'Fecha inválida',
  'wx.de': 'De',
  'wx.defina_uma_velocidade_fixa_ou_rode_um_teste_automatico_para_': 'Define una velocidad fija o ejecuta una prueba.',
  'wx.desabilitar_controles': 'Desactivar controles',
  'wx.destaca_o_botao_central_de_play': 'Destaca el botón central de play',
  'wx.dica_clique_na_caixa_no_player_para_mover_e_redimensionar': 'Consejo: haz clic en la caja del player para mover y redimensionar.',
  'wx.digite_o_novo_texto': 'Escribe el nuevo texto',
  'wx.digite_o_texto_da_subheadline_opcional': 'Escribe el texto del subtítulo (opcional)',
  'wx.digite_o_texto_principal': 'Escribe el texto principal...',
  'wx.digite_o_texto_secundario': 'Escribe el texto secundario...',
  'wx.digite_um_nome_para_a_variacao': 'Escribe un nombre para la variación',
  'wx.direita': 'Derecha',
  'wx.distribuicao_de_trafego_atualizada_com_sucesso': '¡Distribución de tráfico actualizada con éxito!',
  'wx.duracao_da_exibicao_segundos': 'Duración de la exhibición (segundos)',
  'wx.duracao_do_teste': 'Duración de la prueba',
  'wx.editar': 'Editar',
  'wx.em_execucao': 'En ejecución',
  'wx.encerra_o_teste_e_mantem_a_variacao_vencedora_ativa': 'Finaliza la prueba y mantiene la variación ganadora activa.',
  'wx.engajamento': 'Engagement',
  'wx.enviar_imagem': 'Subir imagen',
  'wx.enviar_icone_svg_recomendado': 'Subir icono (SVG recomendado)',
  'wx.envie_uma_imagem_para_o_template_personalizado': 'Sube una imagen para la plantilla personalizada',
  'wx.escolha_o_estilo_visual_do_botao_de_play_do_seu_player': 'Elige el estilo visual del botón de play de tu player.',
  'wx.escolha_o_icone': 'Elige el icono',
  'wx.escolha_um_modelo_visual_para_o_overlay': 'Elige una plantilla visual para el overlay',
  'wx.escolha_um_video_da_biblioteca': 'Elige un video de la biblioteca',
  'wx.esconder_inicio': 'Ocultar inicio',
  'wx.esquerda': 'Izquierda',
  'wx.estilo': 'Estilo',
  'wx.estilo_avancado': 'Estilo avanzado',
  'wx.estilo_da_barra': 'Estilo de la barra',
  'wx.estilo_da_headline': 'Estilo del titular',
  'wx.estilo_da_subheadline': 'Estilo del subtítulo',
  'wx.estilo_do_autoplay': 'Estilo del Autoplay',
  'wx.estilo_do_icone': 'Estilo del icono',
  'wx.estilo_e_comportamento': 'Estilo y comportamiento',
  'wx.evite_conclusoes_precipitadas_com_poucos_dados_espere_atingi': 'Evita conclusiones precipitadas con pocos datos — espera alcanzar los valores mínimos recomendados.',
  'wx.ex_ative_o_som_para_melhor_experiencia': 'Ej.: Activa el sonido para mejor experiencia',
  'wx.ex_seu_video_ja_comecou': 'Ej.: Tu video ya comenzó',
  'wx.ex_assista_ate_o_final_para_descobrir_o_metodo_que_esta_gera': 'Ej.: Mira hasta el final para descubrir el método que está generando ventas todos los días',
  'wx.ex_autoplay_moderno_conversao_limpa': 'Ej.: Autoplay Moderno, Conversión Limpia...',
  'wx.ex_botao_de_checkout': 'Ej.: Botón de Checkout',
  'wx.ex_headline_moderna_conversao_limpa': 'Ej.: Titular Moderno, Conversión Limpia...',
  'wx.ex_mostrar_botao_de_compra': 'Ej.: Mostrar botón de compra',
  'wx.ex_seu_video_ja_comecou_2': 'Ej.: Tu video ya comenzó',
  'wx.ex_toque_para_ativar_o_som': 'Ej.: Toca para activar el sonido',
  'wx.ex_ativo_visivel_destaque': 'Ej.: activo, visible, destacado',
  'wx.ex_color_background_display': 'Ej.: color, background, display',
  'wx.excluir': 'Eliminar',
  'wx.excluir_variacao': '¿Eliminar variación?',
  'wx.exibe_modal_para_retomar_reproducao_de_onde_o_usuario_parou': 'Retoma el video donde el usuario se detuvo.',
  'wx.exibe_uma_imagem_estatica_no_lugar_do_player': 'Muestra una imagen estática en lugar del player',
  'wx.exibe_uma_mensagem_sutil_informando_que_o_video_iniciou_auto': 'Avisa que el video inició automáticamente.',
  'wx.exibir_video_original': 'Mostrar video original',
  'wx.exibir_barra_de_controles': 'Mostrar barra de controles',
  'wx.exibicao_por_dispositivo': 'Visualización por dispositivo',
  'wx.experiencia_imersiva': 'Experiencia inmersiva',
  'wx.facilite_o_controle_de_tempo_durante_a_reproducao': 'Facilita el control de tiempo durante la reproducción.',
  'wx.familia_da_fonte': 'Familia de la fuente',
  'wx.finalizar_teste_a_b': '¿Finalizar prueba A/B?',
  'wx.forma_do_botao': 'Forma del botón',
  'wx.formato_da_imagem': 'Formato de la imagen',
  'wx.fundo': 'Fondo',
  'wx.fundo_botao': 'Fondo del botón',
  'wx.fundo_da_headline': 'Fondo del titular',
  'wx.futuras_integracoes': 'Integraciones futuras',
  'wx.gratis_segredo_exclusivo_novo': 'Gratis, Secreto, Exclusivo, Nuevo',
  'wx.html_personalizado': 'HTML personalizado',
  'wx.habilita_a_sincronizacao_inteligente_de_acoes_com_o_video': 'Activa la sincronización inteligente de acciones con el video',
  'wx.habilita_o_botao_de_acao_no_video_selecionado': 'Activa el botón de acción en el video seleccionado',
  'wx.headline': 'Titular',
  'wx.headline_principal': 'Titular principal',
  'wx.headline_nao_ativada': 'Titular no activado',
  'wx.headline_por_dispositivo': 'Titular por dispositivo',
  'wx.herdando_valores_do_desktop_altere_para_personalizar': 'Heredando valores del Desktop. Cambia para personalizar.',
  'wx.hover_fundo': 'Hover fondo',
  'wx.hover_texto': 'Hover texto',
  'wx.ideal_para_videos_corporativos_e_educacionais': 'Ideal para videos corporativos y educativos',
  'wx.imagem': 'Imagen',
  'wx.imagem_desktop': 'Imagen Desktop',
  'wx.imagem_mobile': 'Imagen Mobile',
  'wx.imagem_carregada_com_sucesso': '¡Imagen cargada con éxito!',
  'wx.imagem_de_fundo': 'Imagen de fondo',
  'wx.imagem_do_autoplay': 'Imagen del Autoplay',
  'wx.imagem_do_continuar_de_onde_parou': 'Imagen de Continuar Viendo',
  'wx.imagem_removida': 'Imagen eliminada',
  'wx.inferior_direito': 'Inferior Derecho',
  'wx.inferior_esquerdo': 'Inferior Izquierdo',
  'wx.inicia_o_video_automaticamente_ao_entrar_na_pagina': 'Inicia el video automáticamente al entrar en la página.',
  'wx.iniciar_sem_som': 'Iniciar sin sonido',
  'wx.interacao_com_video': 'Interacción con el video',
  'wx.inicio': 'Inicio',
  'wx.inicio_mm_ss': 'Inicio (mm:ss)',
  'wx.link_de_destino': 'Enlace de destino',
  'wx.link_do_botao': 'Enlace del botón',
  'wx.loop_continuo': 'Loop continuo',
  'wx.mantem_o_video_padrao_neste_dispositivo': 'Mantiene el video predeterminado en este dispositivo',
  'wx.melhor_para_captar_atencao_e_criar_urgencia': 'Mejor para captar atención y crear urgencia',
  'wx.melhor_plataforma': 'Mejor plataforma',
  'wx.mensagem': 'Mensaje',
  'wx.mensagem_personalizada': 'Mensaje personalizado',
  'wx.meu_titulo_personalizado': 'Mi título personalizado',
  'wx.modo_html': 'Modo HTML',
  'wx.modo_html_avancado': 'Modo HTML (Avanzado)',
  'wx.modo_imagem': 'Modo imagen',
  'wx.modo_texto': 'Modo texto',
  'wx.modo_de_exibicao': 'Modo de visualización',
  'wx.mostra_o_progresso_do_video': 'Muestra el progreso del video',
  'wx.mostra_o_progresso_visual': 'Muestra el progreso visual',
  'wx.mostra_os_controles_na_parte_inferior_do_player': 'Muestra los controles en la parte inferior del player',
  'wx.mostra_um_video_diferente_apenas_neste_dispositivo': 'Muestra un video diferente solo en este dispositivo',
  'wx.mostrar_imagem': 'Mostrar imagen',
  'wx.mostrar_barra_de_progresso': 'Mostrar barra de progreso',
  'wx.mostrar_contagem_regressiva': 'Mostrar cuenta regresiva',
  'wx.mostrar_controles': 'Mostrar controles',
  'wx.mostrar_controles_do_video_durante_o_autoplay': 'Mostrar controles del video durante el autoplay',
  'wx.mostrar_elemento': 'Mostrar elemento',
  'wx.mostre_visualmente_o_avanco_do_video_e_aumente_a_retencao': 'Muestra el avance del video y aumenta la retención.',
  'wx.muito_rapido_ou_muito_lento': 'Muy rápido o muy lento',
  'wx.maximo_8_10_palavras': 'Máximo 8-10 palabras',
  'wx.maximo_de_5_modelos_simultaneos_permitidos': 'Máximo 5 plantillas simultáneas permitidas',
  'wx.medio': 'Mediano',
  'wx.metricas_rastreadas': 'Métricas rastreadas:',
  'wx.metricas_atualizadas_para_novo_formato': '¡Métricas actualizadas al nuevo formato!',
  'wx.minimo_2_variacoes': 'Mínimo: 2 variaciones',
  'wx.navegacao_do_video': 'Navegación del video',
  'wx.nenhum_teste_ativo': 'Ninguna prueba activa',
  'wx.nenhum_video_encontrado': 'No se encontraron videos',
  'wx.nenhuma_variacao_configurada': 'Ninguna variación configurada',
  'wx.nome_da_acao': 'Nombre de la acción *',
  'wx.nome_da_classe': 'Nombre de la clase',
  'wx.nome_da_variacao': 'Nombre de la variación',
  'wx.novo_texto': 'Nuevo texto',
  'wx.o_tempo_de_pitch_e': 'El tiempo de Pitch es:',
  'wx.oculta_os_controles_ate_a_primeira_interacao': 'Oculta los controles hasta la primera interacción',
  'wx.ocultar_pause': 'Ocultar pausa',
  'wx.ocultar_video': 'Ocultar video',
  'wx.ocultar_elemento': 'Ocultar elemento',
  'wx.opacidade_do_fundo': 'Opacidad del fondo',
  'wx.open_sans': 'Open Sans',
  'wx.open_sans_classic': 'Open Sans (Classic)',
  'wx.ordem_atualizada': 'Orden actualizado',
  'wx.overlay_personalizado': 'Overlay personalizado',
  'wx.para_conteudos_educativos_priorize_o_engajamento_para_vendas': 'Para contenidos educativos, prioriza el engagement; para ventas, prioriza la tasa de conversión',
  'wx.para_usar_a_barra_de_progresso_personalizada_desative_os_con': 'Para usar la barra de progreso personalizada, desactiva los controles del player.',
  'wx.percentual_de_visitantes_que_iniciaram_o_video': 'Porcentaje de visitantes que iniciaron el video',
  'wx.percentual_medio_de_retencao_do_video_assistido': 'Porcentaje promedio de retención del video visto',
  'wx.perfeito_para_vendas_e_call_to_actions': 'Perfecto para ventas y call-to-actions',
  'wx.permita_interacao_com_o_video_e_aumente_controle_retencao_e_': 'Activa los controles del reproductor.',
  'wx.permite_ajustar_o_audio': 'Permite ajustar el audio',
  'wx.permite_que_o_espectador_retome_o_video_de_onde_parou': 'Permite al espectador retomar el video donde lo dejó.',
  'wx.personalizado': 'Personalizado',
  'wx.personalizacao_visual': 'Personalización visual',
  'wx.personalize_a_aparencia_do_autoplay': 'Personaliza la apariencia del Autoplay',
  'wx.personalize_cores_dos_controles_e_da_barra_inferior': 'Personaliza colores de los controles y la barra inferior.',
  'wx.personalize_cores_animacoes_e_efeitos_visuais': 'Personaliza colores, animaciones y efectos visuales',
  'wx.personalize_o_comportamento_do_player_para_cada_tipo_de_disp': 'Ajusta el reproductor para cada dispositivo.',
  'wx.player_simulado': 'Player simulado',
  'wx.plays_unicos': 'Reproducciones únicas',
  'wx.plays_unicos_por_usuario': 'Reproducciones únicas por usuario',
  'wx.posicao': 'Posición',
  'wx.posicao_h': 'Posición H (%)',
  'wx.posicao_v': 'Posición V (%)',
  'wx.posicao_do_botao_no_video': 'Posición del botón en el video',
  'wx.posicao_e_tamanho': 'Posición y tamaño',
  'wx.preencha_headline_ou_subheadline': 'Completa el titular o subtítulo.',
  'wx.preencha_todos_os_campos_obrigatorios': 'Completa todos los campos obligatorios',
  'wx.preview': 'Vista previa',
  'wx.preview_configurado': 'Vista previa configurada:',
  'wx.preview_de_frames_na_timeline': 'Vista previa de cuadros en la línea de tiempo',
  'wx.preview_disponivel_no_player_real': 'Vista previa disponible en el player real',
  'wx.preview_do_botao': 'Vista previa del botón',
  'wx.preview_do_player': 'Vista previa del player',
  'wx.progresso_do_teste': 'Progreso de la prueba',
  'wx.pre_visualizacoes_de_estilos': 'Vistas previas de estilos',
  'wx.pre_visualizacao_ilustrativa': 'Vista previa ilustrativa',
  'wx.pre_visualizacao_ilustrativa_da_barra_de_progresso': 'Vista previa ilustrativa de la barra de progreso',
  'wx.pre_visualizacao_ilustrativa_do_html_personalizado': 'Vista previa ilustrativa del HTML personalizado',
  'wx.pre_visualizacao_ilustrativa_do_botao_no_video': 'Vista previa ilustrativa del botón en el video',
  'wx.previa_do_botao': 'Vista previa del botón',
  'wx.pulsar': 'Pulso',
  'wx.quando_a_headline_aparece': 'Cuando el titular aparece',
  'wx.quando_a_headline_desaparece': 'Cuando el titular desaparece',
  'wx.quando_ativado_o_video_reinicia_do_inicio_ao_clicar': 'Cuando está activado, el video reinicia desde el inicio al hacer clic',
  'wx.realize_testes_com_pelo_menos_100_visualizacoes_por_velocida': 'Realiza pruebas con al menos 100 visualizaciones por velocidad',
  'wx.recomenda_se_um_minimo_de_800_plays_unicos_por_variacao_para': 'Se recomienda un mínimo de 800 reproducciones únicas por variación para resultados estadísticamente significativos',
  'wx.recomendado': 'Recomendado',
  'wx.recomecar_video_apos_o_fim': 'Reiniciar video después del final',
  'wx.reiniciar_video': 'Reiniciar video',
  'wx.remove_completamente_o_player_neste_dispositivo': 'Elimina completamente el player en este dispositivo',
  'wx.remove_distracao_visual': 'Elimina la distracción visual',
  'wx.remover_imagem': 'Eliminar imagen',
  'wx.repetir_video_loop': 'Repetir video (loop)',
  'wx.reproducao_automatica': 'Reproducción automática',
  'wx.retencao_no_pitch': 'Retención en el Pitch',
  'wx.retencao_no_pitch_2': 'Retención en el pitch',
  'wx.retomada_automatica': 'Reanudación automática',
  'wx.retomar_reproducao': 'Reanudar reproducción',
  'wx.retangulo': 'Rectángulo',
  'wx.selecione_a_posicao': 'Selecciona la posición',
  'wx.selecione_a_velocidade_desejada': 'Selecciona la velocidad deseada',
  'wx.selecione_o_tamanho': 'Selecciona el tamaño',
  'wx.selecione_pelo_menos_2_modelos_para_iniciar_o_teste': 'Selecciona al menos 2 plantillas para iniciar la prueba',
  'wx.selecione_uma_animacao': 'Selecciona una animación',
  'wx.sem_animacao': 'Sin animación',
  'wx.seu_html_aparecera_aqui': 'Tu HTML aparecerá aquí',
  'wx.seu_html_aqui': 'Tu HTML aquí',
  'wx.seu_video_ja_comecou': 'Tu video ya comenzó',
  'wx.slide_de_baixo': 'Deslizar desde abajo',
  'wx.sobre_a_reproducao_automatica': 'Acerca de la reproducción automática:',
  'wx.som_de_alerta': 'Sonido de alerta',
  'wx.status_do_teste': 'Estado de la prueba',
  'wx.substituir_por_outro_video': 'Sustituir por otro video',
  'wx.solido': 'Sólido',
  'wx.tamanho': 'Tamaño',
  'wx.tamanho_da_fonte': 'Tamaño de la fuente',
  'wx.tamanho_do_icone': 'Tamaño del icono',
  'wx.tamanho_e_posicao': 'Tamaño y posición',
  'wx.tamanho_recomendado_1920x1080px_formatos_jpg_png_gif_maximo_': 'Tamaño recomendado: 1920x1080px • Formatos: JPG, PNG, GIF • Máximo: 10MB',
  'wx.taxa_de_conversao': 'Tasa de conversión',
  'wx.tempo': 'Tiempo',
  'wx.tempo_atual': 'Tiempo actual',
  'wx.tempo_atual_2': 'Tiempo actual',
  'wx.tempo_ate_retomar': 'Tiempo hasta reanudar',
  'wx.tempo_de_exibicao': 'Tiempo de exhibición',
  'wx.tempo_do_video_mm_ss': 'Tiempo del video (mm:ss) *',
  'wx.tempo_para_exibicao_segundos': 'Tiempo para exhibición (segundos)',
  'wx.temporizacao': 'Temporización',
  'wx.teste_a_b_ativado': '¡Prueba A/B activada!',
  'wx.teste_aplicado_com_sucesso': '¡Prueba aplicada con éxito!',
  'wx.teste_finalizado': 'Prueba finalizada',
  'wx.teste_finalizado_com_sucesso': '¡Prueba finalizada con éxito!',
  'wx.texto': 'Texto',
  'wx.texto_botao': 'Texto del botón',
  'wx.texto_inferior': 'Texto inferior',
  'wx.texto_superior': 'Texto superior',
  'wx.texto_branco_em_fundo_claro': 'Texto blanco sobre fondo claro',
  'wx.texto_da_headline': 'Texto del titular',
  'wx.texto_da_subheadline': 'Texto del subtítulo',
  'wx.texto_do_botao': 'Texto del botón',
  'wx.texto_do_botao_principal': 'Texto del botón principal',
  'wx.texto_do_link_secundario': 'Texto del enlace secundario',
  'wx.texto_exibido_no_overlay_para_orientar_o_usuario': 'Texto mostrado en el overlay para orientar al usuario',
  'wx.textos_do_overlay': 'Textos del overlay',
  'wx.ticket_medio': 'Ticket promedio',
  'wx.tipo': 'Tipo',
  'wx.tipo_de_animacao': 'Tipo de animación',
  'wx.tipo_de_acao': 'Tipo de acción *',
  'wx.tipo_de_fundo': 'Tipo de fondo',
  'wx.tipo_de_som': 'Tipo de sonido',
  'wx.tipo_2': 'Tipo:',
  'wx.todos_os_videos': 'Todos los videos',
  'wx.topo_direita': 'Superior Derecha',
  'wx.topo_esquerda': 'Superior Izquierda',
  'wx.topo_pode_funcionar_melhor_que_rodape': 'Arriba puede funcionar mejor que abajo',
  'wx.total_de_visualizacoes_do_video': 'Total de visualizaciones del video',
  'wx.trafego': 'Tráfico',
  'wx.trafego_dividido_igualmente': 'Tráfico dividido equitativamente',
  'wx.termino': 'Fin',
  'wx.termino_previsto': 'Fin previsto:',
  'wx.titulo': 'Título',
  'wx.titulo_opcional': 'Título (opcional)',
  'wx.titulo_do_overlay': 'Título del overlay',
  'wx.uma_mensagem_principal_por_vez': 'Un mensaje principal a la vez',
  'wx.upload_do_icone_personalizado': 'Subida del icono personalizado',
  'wx.usar_cor_do_play': 'Usar color del Play',
  'wx.usar_velocidade_fixa': 'Usar velocidad fija',
  'wx.use_id_para_ids_ou_classe_para_classes_css_exemplo_meubotao_': 'Usa #id para IDs o .clase para clases CSS. Ejemplo: #miBoton o .mi-seccion',
  'wx.use_o_calendario_para_comparar_periodos_semelhantes': 'Usa el calendario para comparar periodos similares',
  'wx.variacao': 'Variación',
  'wx.variacao_adicionada': '¡Variación añadida!',
  'wx.variacao_atualizada': '¡Variación actualizada!',
  'wx.variacao_excluida': 'Variación eliminada',
  'wx.variacao_removida': '¡Variación eliminada!',
  'wx.variacao_salva': '¡Variación guardada!',
  'wx.variacoes': 'Variaciones',
  'wx.velocidade': 'Velocidad',
  'wx.velocidade_fixa': 'Velocidad fija',
  'wx.velocidade_atual': 'Velocidad actual',
  'wx.velocidade_final': 'Velocidad final',
  'wx.velocidade_inicial': 'Velocidad inicial',
  'wx.vis_unicas': 'Vis. únicas',
  'wx.visualizacoes': 'Visualizaciones',
  'wx.visualizacoes_unicas': 'Visualizaciones únicas',
  'wx.visualizacoes_unicas_por_usuario': 'Visualizaciones únicas (por usuario)',
  'wx.voce_parou_em_um_ponto_importante_do_video': 'Te detuviste en un punto importante del video.',
  'wx.volte_para_revisar_momentos': 'Vuelve para revisar momentos',
  'wx.video_de_fundo_inteligente': 'Video de fondo inteligente',
  'wx.ate_2': 'hasta',
  'wx.https_exemplo_com': 'https://ejemplo.com',
  'wx.https_exemplo_com_br': 'https://ejemplo.com',
  'wx.segundos': 'segundos',
  'wx.icone': 'Icono',
  'wx.icone_personalizado': 'Icono personalizado',
  'wx.icone_do_play': 'Icono del Play',
  'wx.analytics_avancados': '• Analíticas avanzadas',
  'wx.animacoes_css': '• Animaciones CSS',
  'wx.ate_3_variacoes_simultaneas': '• Hasta 3 variaciones simultáneas',
  'wx.ctr_click_through_rate': '• CTR (Click-through Rate)',
  'wx.compressao_automatica': '• Compresión automática',
  'wx.configuracoes_de_qualidade': '• Configuración de calidad',
  'wx.controle_total_com_codigo': '• Control total con código',
  'wx.cores_personalizaveis': '• Colores personalizables',
  'wx.cores_que_nao_contrastam': '• Colores que no contrastan:',
  'wx.crie_urgencia': '• Crea urgencia:',
  'wx.codigo_de_embed': '• Código embed',
  'wx.divisao_automatica_de_trafego': '• División automática de tráfico',
  'wx.editor_visual_intuitivo': '• Editor visual intuitivo',
  'wx.engajamento_2': '• Engagement',
  'wx.headlines_genericas': '• Titulares genéricos:',
  'wx.integracao_com_cdn': '• Integración con CDN',
  'wx.integracao_com_js': '• Integración con JS',
  'wx.mantenha_legivel': '• Mantén legible:',
  'wx.metricas_em_tempo_real': '• Métricas en tiempo real',
  'wx.multiplos_focos': '• Múltiples focos:',
  'wx.otimizacao_de_performance': '• Optimización de rendimiento',
  'wx.para_desenvolvedores': '• Para desarrolladores',
  'wx.posicao_que_cobre_conteudo': '• Posición que cubre contenido:',
  'wx.preview_em_tempo_real': '• Vista previa en tiempo real',
  'wx.seja_especifico': '• Sé específico:',
  'wx.tamanho_maximo_2mb': '• Tamaño máximo: 2MB',
  'wx.taxa_de_conversao_2': '• Tasa de conversión',
  'wx.tempo_medio_de_exibicao': '• Tiempo promedio de exhibición',
  'wx.teste_posicoes': '• Prueba posiciones:',
  'wx.texto_muito_longo': '• Texto demasiado largo:',
  'wx.upload_de_imagens_gifs': '• Subir imágenes/GIFs',
  'wx.use_numeros': '• Usa números:',
  'wx.versoes_desktop_mobile': '• Versiones desktop/mobile',
  'wx.alguns_navegadores_podem_restringir_o_audio_automatico_mesmo': '⚠️ Algunos navegadores pueden restringir el audio automático incluso después de la interacción del visitante.',
  'wx.velocidades_muito_altas_acima_de_1_5x_podem_prejudicar_a_com': '⚠️ Velocidades muy altas (por encima de 1.5x) pueden perjudicar la comprensión y confianza en el contenido.',
  'wx.requisito_autoplay_ativo': '⚡ Requisito: Autoplay activo',
  'wx.faca': '✅ Haz',
  'wx.modos_de_criacao': '🎨 Modos de creación',
  'wx.melhores_praticas': '🎯 Mejores prácticas',
  'wx.dicas_de_templates': '💡 Consejos de plantillas',

  'wx2.fontWeight': 'Peso de la Fuente',
  'wx2.all': 'Todas',
  'wx2.none': 'Ninguna',
  'wx2.fadeIn': 'Fade In',
  'wx2.fadeInHyphen': 'Fade-in',
  'wx2.loadBlock': 'Cargar bloque',
  'wx2.status': 'Estado',
  'wx2.statusColon': 'Estado:',
  'wx2.upload': 'Subir',
  'wx2.autoplayMessages': 'Mensajes del Autoplay',
  'wx2.showBorder': 'Mostrar borde',
  'wx2.saveProgressAuto': 'Guarda automáticamente el progreso del espectador',
  'wx2.continueWatching': 'Continuar Viendo',
  'wx2.pictureInPicture': 'Picture-in-Picture',
  'wx2.initialCounterValue': 'Valor inicial del contador',
};

const dicts: Record<string, Dict> = {
  'pt-BR': ptBR,
  en,
  es,
  fr: ptBR,
  it: ptBR,
};

export function tw4(key: string): string {
  const lang = getLanguage();
  const source = ptBR[key];
  if (source && sourceMessages[lang]?.[source] !== undefined) return tx(source);
  return dicts[lang]?.[key] ?? dicts['pt-BR'][key] ?? key;
}
